import Vue from 'vue';
import Uri from 'jsuri';
import axios from 'axios';

import { AsYouType } from 'libphonenumber-js';

import emitter from '../../helpers/bus';

import { getAts, getAtsRecord } from '../../helpers/productData';

import { attachPacksOf } from '../../helpers/attachpacks';

import { outOfStockResetCache, isBeardHeadAvailable } from '../../helpers/outofstock';

import * as constants from '../../constants';

const EMPTY_IMAGE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const MIN_IMAGE_SIZE = 80000;

// Debounce timer for price updates.
let updatePriceTimer = null;

const stableSort = (arr, compare) => arr
    .map((item, index) => ({ item, index }))
    .sort((a, b) => compare(a.item, b.item) || a.index - b.index)
    .map(({ item }) => item);

function getCategoryName(option, translations) {
    if (option.custom == null) {
        return null;
    }

    if (option.custom['fun-featured'] === 'true') {
        return translations('FEATURED');
    }

    if (option.custom['fun-featured-default'] === 'true') {
        return translations('FEATURED');
    }

    return option.custom['fun-category'];
}

/**
 * Synchronizes seenGroupsByController and seenGroups attributes.
 */
function updateSeenAndAlertGroups(state) {
    // state.viewController.id is the current controller.
    if (state.viewController?.id == null) {
        return;
    }

    state.seenGroupsByController[state.viewController.id] = [...state.seenGroups];
    state.alertGroupsByController[state.viewController.id] = [...state.alertGroups];
}

/**
 * Synchronizes boxNamesByController and boxName attributes.
 */
function updateBoxNames(state) {
    if (state.viewController?.id == null) {
        return;
    }

    state.boxNamesByController[state.viewController.id] = state.boxName;
}

/**
 * Synchronizes selectedSwatchColorsByController and selectedSwatchColors attributes.
 */
function updateSelectedSwatchColors(state) {
    if (state.viewController?.id == null) {
        return;
    }

    state.selectedSwatchColorsByController[state.viewController.id] = state.selectedSwatchColors;
}

/**
 * Gets pets of a controller.
 */
function petsOf(controller, state) {
    let placement = controller.state.find((s) => constants.PLACEMENT_PETS.includes(s.placement.code));

    if (placement == null) {
        return [];
    }

    placement = placement.placement;

    const components = [];

    placement.components.forEach((c) => {
        if (c.placeholder) {
            return;
        }

        const pic = c.placementIndependentCode;

        // Is it selected.
        let quantity = 0;
        const petIcons = [];

        controller.state.forEach((s) => {
            if (s.placement.code !== constants.PLACEMENT_PET_PREVIEW && s.placement.code !== constants.PLACEMENT_PET_PREVIEW_SECONDARY) {
                if (s.component.placementIndependentCode === pic) {
                    quantity += s.component.quantityMultiplier || 1;
                    petIcons.push(
                        `https://api.images.drivecommerce.com/api/v1/image/funko/CZ-Icon-${s.component.custom['fun-sku']}?fmt=png-alpha`,
                    );
                }
            }
        });

        const price = controller.getPrice(c);

        components.push({
            code: c.code,
            quantity,
            placementIndependentCode: pic,
            component: c,
            price,
            category: getCategoryName(c, state.translations) || 'Uncategorized',
            canAdd: controller.isAvailable(c, quantity),
            image: `https://api.images.drivecommerce.com/api/v1/image/funko/CZ-Icon-v2-${c.custom['fun-sku']}?fmt=webp&qlt=40&rwid=562&rhei=562`,
            type: 'pet',
            icon: petIcons,
        });
    });

    // Current pets quantity.
    const total = components.reduce((current, c) => current + (c.quantity ?? 0), 0);

    if (total >= constants.PETS_LIMIT) {
        components.forEach((c) => {
            c.canAdd = false;
        });
    }

    const sorted = stableSort(components, (a, b) => {
        if (a.category < b.category) {
            return -1;
        }

        if (a.category > b.category) {
            return 1;
        }

        return 0;
    });

    return sorted;
}

/**
 * Download an image and resize to specs.
 */
async function fetchAndResizeImageAsBase64(url, width, height) {
    let useUrl = url;

    if (useUrl.startsWith('//')) {
        useUrl = `https:${url}`;
    }

    const response = await fetch(useUrl);

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
        const img = new Image();

        const objectURL = URL.createObjectURL(blob);

        img.src = objectURL;

        img.onload = () => {
            const canvas = document.createElement('canvas');

            const ctx = canvas.getContext('2d');

            canvas.width = width;
            canvas.height = height;

            ctx.drawImage(img, 0, 0, width, height);

            const base64Image = canvas.toDataURL('image/png');

            canvas.width = 0;
            canvas.height = 0;

            URL.revokeObjectURL(objectURL);

            resolve(base64Image);
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectURL);

            reject(new Error('Failed to load image'));
        };
    });
}

function getSpecialBoxes(state, getters, rootState, options) {
    if (state.mainController == null) {
        return null;
    }

    const boxes = [];

    for (let i = 1; i < 200; i += 1) {
        const required = state.mainController.blueprint.custom[`fun-special-box-${i}`];
        const sku = state.mainController.blueprint.custom[`fun-special-box-${i}-sku`];
        const component = state.mainController.blueprint.custom[`fun-special-box-${i}-component`];
        const tag = state.mainController.blueprint.custom[`fun-special-box-${i}-rule-tag`];
        const image = state.mainController.blueprint.custom[`fun-special-box-${i}-image`];
        const header = state.mainController.blueprint.custom[`fun-special-box-${i}-header`];
        const text = state.mainController.blueprint.custom[`fun-special-box-${i}-text`];
        const cta = state.mainController.blueprint.custom[`fun-special-box-${i}-cta`];

        if (required && required !== 'none') {
            // If the tag enabled?
            let haveRequiredTag = false;

            // eslint-disable-next-line no-restricted-syntax
            for (const s of state.mainController.state) {
                if (s.component.tags && s.component.tags.includes(required)) {
                    haveRequiredTag = true;
                    break;
                }
            }

            if (!haveRequiredTag) {
                getters.popControllers.forEach((c) => {
                    if (haveRequiredTag) {
                        return;
                    }

                    // eslint-disable-next-line no-restricted-syntax
                    for (const s of c.state) {
                        if (s.component.tags && s.component.tags.includes(required)) {
                            haveRequiredTag = true;
                            break;
                        }
                    }
                });
            }

            if (!haveRequiredTag) {
                if (tag) {
                    state.mainController.removeTag(tag);

                    if (options && options.onRemove) {
                        options.onRemove();
                    }
                }

                // eslint-disable-next-line no-continue
                continue;
            }
        }

        if (!sku || !image || !header || !text || !component) {
            // Remove rule tag if not available.
            if (tag) {
                state.mainController.removeTag(tag);

                if (options && options.onRemove) {
                    options.onRemove();
                }
            }

            // eslint-disable-next-line no-continue
            continue;
        }

        let boxPlacement = state.mainController.findPlacement(constants.PLACEMENT_BOX);
        if (boxPlacement == null) {
            boxPlacement = state.mainController.findPlacement(constants.PLACEMENT_BOX_PACK);
        }

        // Find the component.
        const box = boxPlacement?.components?.find((x) => x.placementIndependentCode === component);
        if (box == null) {
            // eslint-disable-next-line no-continue
            continue;
        }

        // Can't use state.mainController.isAvailable(box) since the box may not be enabled by rules yet.
        // Get the inventory directly.
        const ats = getAts(rootState, box, state.mainController);

        if (ats == null || ats < 1) {
            // eslint-disable-next-line no-continue
            continue;
        }

        // Is it selected?
        const selectedBox = state.mainController.selectedAtPlacement(boxPlacement.code);

        const selected = selectedBox?.placementIndependentCode === component;

        const exists = boxes.find((x) => x.sku === sku && x.tag === tag);

        if (!exists) {
            boxes.push({
                index: i,
                sku,
                tag,
                placement: boxPlacement.code,
                component: box,
                image,
                header,
                text,
                selected,
                cta,
            });
        }
    }

    return boxes;
}

const getDefaultState = () => ({
    // Widget option as passed from the host site.
    options: {},

    // Settings stored in Customizer.
    globalSettings: null,

    // Default controller parameters.
    defaultControllerOptions: {},

    // Controller that drives the whole data set.
    mainController: null,

    // Controller that drives the current experience.
    viewController: null,

    // Currently selected view.
    currentView: null,

    // Product variants.
    variants: null,

    // Current group to display.
    currentGroup: null,

    // Seen group ids.
    seenGroups: [],

    // Alerted group ids.
    alertGroups: [],

    // Seen group ids, saved for all controllers.
    seenGroupsByController: {},

    // Alerted group ids, saved for all controllers.
    alertGroupsByController: {},

    // Box names, saved for all controllers.
    boxNamesByController: {},

    // Dirty state by controller.
    dirtyByController: {},

    // Active color swatch colors for hair/hats, beard steps
    selectedSwatchColors: {
        'Hair & Hats': null,
        Beard: null,
    },

    // Active color swatch colors for hair/hats, beard steps by controller
    selectedSwatchColorsByController: {},

    // Main pop icon.
    mainIcon: null,

    // Box name.
    boxName: '',

    // Contact name.
    contactName: '',

    // Contact phone.
    contactPhone: '',

    // Used to prevent craziness on mobile when the keyboard is open.
    isKeyboardOpen: false,

    // Last calculated cart.
    cart: null,

    // Pets by controller.
    petsByController: {},

    // Attach packs by controller.
    attachPacksByController: {},

    // Recently printed tickets.
    savedTickets: [],

    // Have the printing been attempted.
    printAttempted: false,

    // Last pop version when the printing as attempted.
    printAttemptedAtVersion: null,

    // Config version.
    configurationVersion: 1,

    // Save recipe override.
    recipeOverride: false,

    // Used by the Pop rotate left and right arrows.
    rotation: null,

    // Used by the ModelPreviewControls rotate left and right arrows.
    previewRotation: null,

    // Used to enable/disable features.
    isMobile: false,

    // Used to enable/disable features on the mobile app.
    isMobileApp: false,

    // Used to update back button behavior while a sub-menu is open
    isSubMenuOpen: false,

    // Used to show and hide the main navigation menu
    isNavMenuOpen: false,

    // Used to determine if the user navigated from the main menu (for button focus)
    didNavigateFromMainMenu: false,

    // Pop preview image.
    popPreviewImage: null,

    // Pop preview image by controller.
    popPreviewImageByController: {},

    // Pop cart image by controller.
    popCartImageByController: {},

    // Pop cart image by controller.
    popCartImageBySpecificController: {},

    // Box preview image.
    boxPreviewImage: null,

    // Attach Packs preview image.
    attachPacksPreviewImage: null,

    // Last printed ticket.
    lastPrintRecipe: null,

    // Temporarily do not capture previews.
    // Used when previewing attach packs.
    noSavePreviews: null,

    // i18n Translations (mapping function).
    translations: null,

    // i18n Translations (raw).
    translationsRaw: null,

    // Loaded recommendations.
    recommendations: null,

    // Selected recommendations.
    selectedRecommendations: null,

    // Saved recipe data.
    savedRecipe: null,

    // Gen AI
    aiPreview: null,
    aiSelections: null,
    aiMatchError: null,
    lastSaveResult: null,

    // Default box.
    defaultBox: null,
});

export default {
    state: getDefaultState(),

    getters: {
        /**
         * Could the design be built?
         */
        canBuild(state, getters) {
            return getters.canBuildReason == null;
        },

        /**
         * Could the design be built?
         */
        canBuildReason(state) {
            if (state.viewController) {
                let can = true;

                // eslint-disable-next-line no-restricted-syntax
                for (const s of state.viewController.state) {
                    if (s.placement.custom['fun-required'] === 'true') {
                        const hasAnyAvailable = (s.placement.components || []).some((c) => !c.placeholder && state.viewController.isAvailable(c));

                        // If this step has components available...
                        // Then one of them should be selected (not a placeholder)
                        // If there are no components available...
                        // Then it's ok to have a placeholder.
                        if (hasAnyAvailable) {
                            if (s.component.placeholder) {
                                can = false;
                            }
                        }
                    }
                }

                // eslint-disable-next-line no-console
                console.log(`%cCan build? ${can}`, 'color: #ff0000; font-weight: bold;');

                if (!can) {
                    return 'component';
                }

                return null;
            }

            return 'controller';
        },

        navigationGroups(state) {
            if (state.viewController == null) {
                return [];
            }

            const navGroups = state.viewController.blueprint.groups.filter((g) => g.custom && g.custom['fun-show-in-nav'] === 'true');

            return navGroups;
        },

        /**
         * Calculate next available group.
         */
        nextGroup(state, getters) {
            if (state.viewController == null) {
                return null;
            }

            const navGroups = state.viewController.blueprint.groups.filter((g) => g.custom && g.custom['fun-show-in-nav'] === 'true');
            // No head/lashes step for non-babies
            const navGroupsFiltered = getters.isBaby ? navGroups : navGroups.filter((g) => g.code !== 'Head');

            let group = state.currentGroup;

            for (; ;) {
                // eslint-disable-next-line no-loop-func
                const index = navGroupsFiltered.findIndex((g) => g.code === group.code);

                if (index >= 0) {
                    group = navGroupsFiltered[index + 1];

                    // Is there a next group?
                    if (group) {
                        let anyAvailable = false;

                        for (let placementIndex = 0; placementIndex < group.placements.length; placementIndex += 1) {
                            const placement = group.placements[placementIndex];

                            for (let componentIndex = 0; componentIndex < placement.components.length; componentIndex += 1) {
                                const component = placement.components[componentIndex];

                                if (!component.placeholder && state.viewController.isAvailable(component)) {
                                    anyAvailable = true;
                                    break;
                                }
                            }

                            if (anyAvailable) {
                                break;
                            }
                        }

                        // Special handling for pets.
                        if (group && group.code === 'Pets') {
                            const { pets } = getters;

                            anyAvailable = pets && pets.length > 0;
                        }

                        // Special handling for attach packs.
                        if (group && group.code === 'Packs') {
                            const { attachPacks } = getters;

                            anyAvailable = attachPacks && attachPacks.length > 0;
                        }

                        // Special handling for name & looks good steps.
                        if (group?.code === 'Name' || group?.code === 'Review') {
                            anyAvailable = true;
                        }

                        // Check if beards are available in the current skin tone.
                        if (group && group.code === 'Beard') {
                            anyAvailable = isBeardHeadAvailable(state.viewController);
                        }

                        if (anyAvailable) {
                            break;
                        }
                    } else {
                        group = null;
                        break;
                    }
                } else {
                    // No index, weird. Where are we?
                    group = null;
                    break;
                }
            }

            return group;
        },

        /**
         * Calculate previous group.
         */
        previousGroup(state, getters, rootState) {
            if (state.viewController == null) {
                return null;
            }

            const navGroups = state.viewController.blueprint.groups.filter((g) => g.custom && g.custom['fun-show-in-nav'] === 'true');
            // No head/lashes step for non-babies
            const navGroupsFiltered = getters.isBaby ? navGroups : navGroups.filter((g) => g.code !== 'Head');

            let group = state.currentGroup;

            for (; ;) {
                // eslint-disable-next-line no-loop-func
                const index = navGroupsFiltered.findIndex((g) => g.code === group.code);

                if (index >= 0) {
                    group = navGroupsFiltered[index - 1];

                    // Is there a next group?
                    if (group) {
                        let anyAvailable = false;

                        for (let placementIndex = 0; placementIndex < group.placements.length; placementIndex += 1) {
                            const placement = group.placements[placementIndex];

                            for (let componentIndex = 0; componentIndex < placement.components.length; componentIndex += 1) {
                                const component = placement.components[componentIndex];

                                if (!component.placeholder && state.viewController.isAvailable(component)) {
                                    anyAvailable = true;
                                    break;
                                }
                            }

                            if (anyAvailable) {
                                break;
                            }
                        }

                        // Check explicitly if we have attach packs.
                        if (group && group.code === 'Packs') {
                            let packs = null;

                            if (rootState.isSpellingBee) {
                                packs = attachPacksOf(state.viewController).filter((x) => x.branding === 'sb');
                            } else {
                                packs = attachPacksOf(state.viewController).filter((x) => x.branding !== 'sb');
                            }

                            if (packs && packs.length === 0) {
                                anyAvailable = false;
                            }
                        }

                        // Check if beards are available in the current skin tone.
                        if (group && group.code === 'Beard') {
                            anyAvailable = isBeardHeadAvailable(state.viewController);
                        }

                        if (anyAvailable) {
                            break;
                        }
                    } else {
                        group = null;
                        break;
                    }
                } else {
                    // No index, weird. Where are we?
                    group = null;
                    break;
                }
            }

            return group;
        },

        /**
         * Get selected color.
         */
        selectedColor(state) {
            if (state.viewController == null) {
                return null;
            }
            const color = state.viewController.selectedAtPlacement(constants.PLACEMENT_SKIN_COLOR);

            return (color !== null && !color.placeholder) ? color : null;
        },

        /**
         * Get selected body.
         */
        selectedBodyType(state) {
            if (state.viewController == null) {
                return null;
            }

            const body = state.viewController.selectedAtPlacement(constants.PLACEMENT_BODY);

            return (body !== null && !body.placeholder) ? body : null;
        },

        /**
         * Checks if a head with lashes is selected.
         */
        selectedLashes(state) {
            if (state.viewController == null) {
                return false;
            }

            const head = state.viewController.selectedAtPlacement(constants.PLACEMENT_HEAD);

            return head != null && head.tags != null && head.tags.includes('lashes');
        },

        /**
         * Has customization started.
         */
        isStarted(state, getters) {
            return getters.selectedColor && getters.selectedBodyType;
        },

        pets(state) {
            if (state.viewController == null) {
                return [];
            }

            return petsOf(state.viewController, state);
        },

        attachPacks(state, getters, rootState) {
            if (state.viewController == null) {
                return [];
            }

            if (rootState.isSpellingBee) {
                return attachPacksOf(state.viewController).filter((x) => x.branding === 'sb');
            }

            return attachPacksOf(state.viewController).filter((x) => x.branding !== 'sb');
        },

        sbAttachPacks(state) {
            if (state.viewController == null) {
                return [];
            }

            return attachPacksOf(state.viewController).filter((x) => x.branding === 'sb');
        },

        sticker(state) {
            if (state.viewController == null) {
                return null;
            }

            const sticker = state.viewController.selectedAtPlacement(constants.PLACEMENT_STICKERS);

            return sticker == null || sticker.placeholder ? null : sticker;
        },

        protector(state) {
            if (state.mainController == null) {
                return null;
            }

            const protector = state.mainController.selectedAtPlacement(constants.PLACEMENT_PROTECTOR);

            return protector == null || protector.placeholder ? null : protector;
        },

        preorderDate(state, getters, rootState) {
            let shipsOn = null;
            let shipsOnDate = null;

            state.viewController.state.forEach((s) => {
                // const inventory = getInventory(s.component);
                const inventory = getAtsRecord(rootState, s.component, state.viewController);

                if (inventory && inventory.ats === 0 && inventory.ato > 0) {
                    const date = inventory.futureDate;

                    if (date) {
                        const parts = date.split('/');
                        const days = (+parts[0] * 31) + (+parts[1]);

                        if (shipsOn == null) {
                            shipsOn = date;
                            shipsOnDate = days;
                        } else if (days > shipsOnDate) {
                            shipsOn = date;
                            shipsOnDate = days;
                        }
                    }
                }
            });

            return shipsOn;
        },

        cart(state, getters, rootState) {
            const items = [];

            // Add dependency for configuration bumps.
            if (state.configurationVersion == null) {
                return items;
            }

            if (state.mainController == null) {
                return items;
            }

            let firstPop = true;

            // eslint-disable-next-line no-restricted-syntax
            for (const pop of getters.popControllers) {
                const main = pop.selectedAtPlacement(constants.PLACEMENT_MAIN);
                const previewImage = getters.isTwoPack ? state.popCartImageBySpecificController[pop.id] : state.popPreviewImage;

                let mainPrice = null;

                if (firstPop) {
                    mainPrice = pop.getPrice(main);

                    // On e-commerce, the price is determined by the box.
                    // Actually find a selected box and price that.
                    let box = pop.selectedAtPlacement(constants.PLACEMENT_BOX);

                    if (box) {
                        const hasPrice = pop.getPrice(box);
                        if (hasPrice) {
                            mainPrice = hasPrice;
                        }
                    }

                    box = state.mainController.selectedAtPlacement('POPBoxPack');

                    if (box) {
                        const hasPrice = pop.getPrice(box);

                        if (hasPrice) {
                            mainPrice = hasPrice;
                        }
                    }

                    firstPop = false;
                }

                if (main) {
                    items.push({
                        id: pop.id,
                        type: 'main',
                        name: main.description[0].name,
                        shortName: main.description[0].name,
                        icon: [previewImage ?? 'https://api.images.drivecommerce.com/api/v1/image/funko/CZ-Icon-PopSmall'],
                        counts: [1],
                        placement: constants.PLACEMENT_MAIN,
                        price: mainPrice,
                        canEdit: true,
                        canRemove: false,
                        quantity: 1,
                        isFirstPop: getters.popControllers.indexOf(pop) === 0,
                    });

                    const preorder = getters.preorderDate;

                    if (preorder) {
                        items.push({
                            type: 'preorder',
                            preorder,
                            quantity: 1,
                            price: 0,
                        });
                    }
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const popState of pop.state) {
                    const cartComponent = pop.selectedAtPlacement(popState.placement.code);
                    const isBuddyPack = popState.placement.code === 'POPBuddyPack';
                    const packImages = [];
                    const packBuddyNames = [];

                    if (cartComponent.custom?.['fun-show-in-cart'] === 'true') {
                        const iconUrl = cartComponent.custom?.image?.replace('&bgc=3396f0', '')?.replace('fmt=jpeg', 'fmt=webp');

                        let usePrice = null;

                        if (cartComponent.custom?.['fun-show-price-in-cart'] !== 'false') {
                            usePrice = pop.getPrice(cartComponent);
                        }

                        if (!rootState.ecommerce.firstAccessoryZeroPricePerBuildEnabled) {
                            if (usePrice == null || usePrice === 0) {
                                // eslint-disable-next-line no-continue
                                continue;
                            }
                        }

                        if (isBuddyPack) {
                            const custom = cartComponent?.custom;

                            for (let i = 2; i <= 5; i += 1) {
                                if (custom[`fun-modal-${i}-image`]) {
                                    packImages.push(custom[`fun-modal-${i}-image`]);
                                    packBuddyNames.push(custom[`fun-modal-${i}-image-alt`]);
                                }
                            }
                        }

                        items.push({
                            id: cartComponent.code,
                            controllerId: pop.id,
                            type: isBuddyPack ? 'buddyPack' : 'extra',
                            name: cartComponent.description[0].name,
                            icon: [iconUrl],
                            placement: popState.placement.code,
                            price: usePrice,
                            canEdit: false,
                            canRemove: isBuddyPack,
                            quantity: 1,
                            packImages,
                            packBuddyNames,
                        });
                    }
                }

                const petsInCart = state.petsByController[pop.id]?.filter((pet) => pet.quantity > 0);
                let id = '';

                if (petsInCart?.length > 0) {
                    petsInCart.forEach((pet, index) => {
                        for (let i = 0; i < pet.quantity; i += 1) {
                            id = `${pop.id}-${index}-${i}`;

                            items.push({
                                id,
                                controllerId: pop.id,
                                type: 'pet',
                                name: pet.component.custom['fun-style-clean'],
                                icon: pet.icon,
                                placement: constants.PLACEMENT_PROTECTOR,
                                price: pet.price,
                                canEdit: false,
                                canRemove: false,
                                quantity: 1,
                            });
                        }
                    });
                }

                const controllersTotal = getters.popControllers.length;
                const lastController = getters.popControllers[controllersTotal - 1];

                if (getters.sticker && pop === lastController) {
                    let stickerPrice = pop.getPrice(getters.sticker);

                    if (stickerPrice) {
                        if (stickerPrice.length > 0) {
                            stickerPrice = stickerPrice[0].value;
                        } else {
                            stickerPrice = 0;
                        }
                    } else {
                        stickerPrice = 0;
                    }

                    items.push({
                        id: getters.sticker.code,
                        type: 'sticker',
                        name: getters.sticker.description[0].name,
                        shortName: getters.sticker.description[0].name,
                        icon: [`https://api.images.drivecommerce.com/api/v1/image/funko/CZ-${getters.sticker.placementIndependentCode}?fmt=png`],
                        counts: [1],
                        placement: constants.PLACEMENT_STICKERS,
                        price: stickerPrice,
                        canEdit: false,
                        canRemove: true,
                        quantity: 1,
                    });
                }

                if (getters.protector && pop === lastController) {
                    let protectorPrice = pop.getPrice(getters.protector);

                    if (protectorPrice) {
                        // Convert from Customizer style price value (array of prices) to E-commerce style value (just the price)
                        if (protectorPrice.length > 0) {
                            protectorPrice = protectorPrice[0].value;
                        }
                    } else {
                        protectorPrice = 0;
                    }

                    items.push({
                        id: 'protector',
                        type: 'protector',
                        name: getters.protector.description[0].name,
                        shortName: getters.protector.description[0].name,
                        icon: [getters.protector.custom.image],
                        counts: [1],
                        placement: constants.PLACEMENT_PROTECTOR,
                        price: protectorPrice,
                        canEdit: false,
                        canRemove: true,
                        quantity: 1,
                    });
                }

                if (pop === lastController) {
                    const discreetPackaging = pop.selectedAtPlacement(constants.PLACEMENT_DISCREET_PACKAGING);
                    if (discreetPackaging != null) {
                        let discreetPackagingPrice = pop.getPrice(discreetPackaging);

                        if (discreetPackagingPrice) {
                            // Convert from Customizer style price value (array of prices) to E-commerce style value (just the price)
                            if (discreetPackagingPrice.length > 0) {
                                discreetPackagingPrice = discreetPackagingPrice[0].value;
                            }
                        } else {
                            discreetPackagingPrice = 0;
                        }

                        items.push({
                            id: 'discreetPackaging',
                            type: 'discreetPackaging',
                            name: discreetPackaging.description[0].name,
                            shortName: discreetPackaging.description[0].name,
                            icon: [discreetPackaging.custom.image],
                            counts: [1],
                            placement: constants.PLACEMENT_DISCREET_PACKAGING,
                            price: discreetPackagingPrice,
                            canEdit: false,
                            canRemove: true,
                            quantity: 1,
                        });
                    }
                }

                const apsInCart = state.attachPacksByController[pop.id]?.filter((ap) => ap.quantity > 0);

                if (apsInCart?.length > 0) {
                    apsInCart.forEach((ap, index) => {
                        for (let i = 0; i < ap.quantity; i += 1) {
                            id = `${pop.id}-${index}-${i}`;

                            items.push({
                                id,
                                controllerId: pop.id,
                                type: 'pet',
                                name: ap.component.custom['fun-style-clean'],
                                icon: ap.icon,
                                placement: constants.PLACEMENT_PROTECTOR,
                                price: ap.price,
                                canEdit: false,
                                canRemove: false,
                                quantity: 1,
                            });
                        }
                    });
                }
            }

            // Add in upsell.
            if (state.selectedRecommendations) {
                // eslint-disable-next-line no-restricted-syntax
                for (const r of state.selectedRecommendations) {
                    if (r.quantity == null || r.quantity <= 0) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    // id: product.id,
                    // quantity: product.inCartQuantityObj.inCartQuantity,
                    // itemUuid: product.inCartQuantityObj.itemUUID,
                    // image: this.getImage(product),
                    // name: product.productName,
                    // price: price ? price.toString() : '',

                    items.push({
                        id: `${r.id}`,
                        type: 'extra',
                        counts: [r.quantity],
                        name: r.name,
                        icon: [r.image],
                        price: r.price,
                        canEdit: false,
                        canRemove: false,
                        quantity: r.quantity,
                    });
                }
            }

            if (getters.seasonalBox?.selected) {
                const { seasonalBox } = getters;

                // eslint-disable-next-line no-unused-vars
                const boxPrice = state.mainController.getPrice(seasonalBox.component);

                items.push({
                    id: 'seasonalbox',
                    type: 'seasonalbox',
                    name: seasonalBox.header,
                    shortName: seasonalBox.header,
                    icon: [seasonalBox.image],
                    counts: [1],
                    placement: constants.PLACEMENT_BOX,
                    price: 0, // boxPrice,
                    canEdit: false,
                    canRemove: true,
                    quantity: 1,
                });
            }

            const specialBox = getters.specialBoxes.find((x) => x.selected);

            if (specialBox) {
                // eslint-disable-next-line no-unused-vars
                const boxPrice = state.mainController.getPrice(specialBox.component);

                items.push({
                    id: 'specialbox',
                    type: 'specialbox',
                    name: specialBox.header,
                    shortName: specialBox.header,
                    icon: [specialBox.image],
                    counts: [1],
                    placement: constants.PLACEMENT_BOX,
                    price: 0, // boxPrice,
                    canEdit: false,
                    canRemove: true,
                    quantity: 1,
                });
            }

            return items;
        },

        cartTotal(state, getters) {
            let price = 0;

            getters.cart.forEach((item) => {
                price += item.price * item.quantity;
            });

            return price;
        },

        availableBuddyPack(state) {
            const pack = state.viewController.findPlacement('POPBuddyPack');

            if (pack) {
                const toSelect = pack.components.find((x) => !x.placeholder);

                return toSelect;
            }

            return null;
        },

        availableProtector(state) {
            if (state.mainController == null) {
                return null;
            }

            const protector = state.mainController.findPlacement(constants.PLACEMENT_PROTECTOR);

            if (protector == null) {
                return null;
            }

            const component = protector.components.find((p) => !p.placeholder && state.mainController.isAvailable(p));

            return component;
        },

        availablePackaging(state) {
            if (state.mainController == null) {
                return null;
            }

            const packaging = state.mainController.findPlacement(constants.PLACEMENT_DISCREET_PACKAGING);

            if (packaging == null) {
                return null;
            }

            const component = packaging.components.find((p) => !p.placeholder && state.mainController.isAvailable(p));

            return component;
        },

        isBaby(state) {
            const body = state.viewController.selectedAtPlacement(constants.PLACEMENT_BODY);

            return body?.custom?.['fun-baby'] === 'true';
        },

        // Is this a two pack product?
        isTwoPack(state) {
            return state.mainController?.blueprint?.custom?.['fun-two-pack'] === 'true';
        },

        /**
         * Get selected ATS locations.
         */
        atsLocations(state, getters, rootState) {
            const controllers = getters.popControllers;

            // Collect available locations, by placement.
            const placements = {};
            const blocked = {};

            // eslint-disable-next-line no-restricted-syntax
            for (const pop of controllers) {
                // eslint-disable-next-line no-restricted-syntax
                for (const s of pop.state) {
                    if (s.component.custom?.['fun-sku'] && rootState.ecommerce.products) {
                        const record = rootState.ecommerce.products[s.component.custom['fun-sku']];
                        if (record) {
                            if (record.inventoryByLocation) {
                                const key = `${pop.id}-${s.placement.code}`;

                                // eslint-disable-next-line no-restricted-syntax
                                for (const location of Object.keys(record.inventoryByLocation)) {
                                    if (record.inventoryByLocation[location]
                                        && (record.inventoryByLocation[location].ats > 0 || record.inventoryByLocation[location].ato > 0)) {
                                        if (placements[key] == null) {
                                            placements[key] = new Set();
                                        }

                                        placements[key].add(location);
                                    }

                                    if (record.inventoryByLocation[location]
                                    && (
                                        record.inventoryByLocation[location].ats == null
                                        || (
                                            (record.inventoryByLocation[location].ats <= 0 && record.inventoryByLocation[location].ato == null)
                                            || (record.inventoryByLocation[location].ats <= 0 && record.inventoryByLocation[location].ato <= 0)
                                        ))) {
                                        if (blocked[key] == null) {
                                            blocked[key] = new Set();
                                        }

                                        blocked[key].add(location);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // eslint-disable-next-line no-restricted-syntax
            for (const p of Object.keys(blocked)) {
                if (placements[p] == null) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const b of blocked[p]) {
                    placements[p].delete(b);
                }
            }

            return placements;
        },

        // Get a list of POP controllers, skipping the main one.
        popControllers(state) {
            if (state.mainController == null) {
                return [];
            }

            const pops = [];

            state.mainController.forAllControllers((c) => {
                if (c !== state.mainController) {
                    pops.push(c);
                }
            });

            if (pops.length === 0) {
                pops.push(state.mainController);
            }

            return pops;
        },

        // Get a list of all controllers, whether it is a single POP or Main + POPs
        allControllers(state) {
            if (state.mainController == null) {
                return [];
            }

            const pops = [];

            state.mainController.forAllControllers((c) => {
                if (c !== state.mainController) {
                    pops.push(c);
                }
            });

            pops.push(state.mainController);

            return pops;
        },

        // Do we support a seasonal box?
        seasonalBox(state, getters, rootState) {
            if (state.mainController == null) {
                return null;
            }

            const sku = state.mainController.blueprint.custom['fun-seasonal-box-sku'];
            const tag = state.mainController.blueprint.custom['fun-seasonal-box-rule-tag'];
            const image = state.mainController.blueprint.custom['fun-seasonal-box-image'];
            const header = state.mainController.blueprint.custom['fun-seasonal-box-header'];
            const text = state.mainController.blueprint.custom['fun-seasonal-box-text'];

            if (!sku || !image || !header || !text) {
                return null;
            }

            // Find the component.
            const box = state.mainController.findPlacement(constants.PLACEMENT_BOX)?.components?.find((x) => x.custom['fun-sku'] === sku);
            if (box == null) {
                return null;
            }

            // Can't use state.mainController.isAvailable(box) since the box may not be enabled by rules yet.
            // Get the inventory directly.
            const ats = getAts(rootState, box, state.mainController);

            if (ats == null || ats < 1) {
                return null;
            }

            // Is it selected?
            const selected = state.mainController.selectedAtPlacement(constants.PLACEMENT_BOX)?.custom?.['fun-sku'] === sku;

            return {
                sku,
                tag,
                placement: constants.PLACEMENT_BOX,
                component: box,
                image,
                header,
                text,
                selected,
            };
        },

        // Do we support special boxes?
        // Generalization of seasonal boxes.
        specialBoxes(state, getters, rootState) {
            return getSpecialBoxes(state, getters, rootState);
        },

        selectedPacks(state, getters) {
            const packs = [];

            getters.popControllers.forEach((controller) => {
                // Make a list of all packs.
                constants.PLACEMENT_PACKS.forEach((placement) => {
                    const component = controller.selectedAtPlacement(placement);

                    if (component && !component.placeholder) {
                        // Do not duplicate packs based on quantity
                        packs.push({
                            controller,
                            component,
                        });
                    }
                });
            });

            return packs;
        },

        // Used to show different states in pet pack components
        petPackInCart(state) {
            const selectedAtPlacement = state.viewController.selectedAtPlacement('POPBuddyPack');

            if (selectedAtPlacement && !selectedAtPlacement.placeholder) {
                return true;
            }

            return false;
        },
    },

    mutations: {
        /**
         * Restart customizer, reset to default state.
         */
        RESTART(state) {
            Object.assign(state, getDefaultState());
        },

        /**
         * Reset state.
         */
        RESET(state) {
            state.seenGroups = [];
            state.alertGroups = [];

            state.seenGroupsByController = {};
            state.alertGroupsByController = {};
            state.boxNamesByController = {};
            state.dirtyByController = {};

            state.selectedSwatchColors = {
                'Hair & Hats': null,
                Beard: null,
            };

            state.selectedSwatchColorsByController = {};

            state.boxName = '';

            state.contactName = '';
            state.contactPhone = '';

            state.mainIcon = null;

            state.currentGroup = null;

            state.petsByController = {};
            state.attachPacksByController = {};

            state.printAttempted = false;

            state.rotation = null;
            state.previewRotation = null;

            state.isMobile = false;

            state.isSubMenuOpen = false;

            state.isNavMenuOpen = false;

            state.popPreviewImage = null;

            state.popPreviewImageByController = {};

            state.popCartImageByController = {};

            state.boxPreviewImage = null;

            state.attachPacksPreviewImage = null;
        },

        /**
         * Saves options to state.
         */
        SET_CUSTOMIZER_OPTIONS(state, options) {
            state.options = options;

            let { locale } = options;
            if (locale) {
                locale = locale.replace('_', '-');
            }

            state.defaultControllerOptions = {
                apiKey: options.apiKey,
                products: options.products,
                preview: options.preview,
                version: options.version,
                disableCaching: options.disableCaching,
                locale,
                site: options.site,
                timeout: 30000,
                recipeTimeout: 60000,
            };

            if (state.defaultControllerOptions.preview === 'null') {
                delete state.defaultControllerOptions.preview;
            }

            if (state.defaultControllerOptions.version === 'null') {
                delete state.defaultControllerOptions.version;
            }

            if (options.endpoint) {
                state.defaultControllerOptions.endpoint = options.endpoint;
            }
        },

        /**
         * Inject translations.
         */
        SET_TRANSLATIONS(state, translations) {
            state.translations = translations;
        },

        /**
         * Inject translations.
         */
        SET_TRANSLATIONS_RAW(state, translations) {
            state.translationsRaw = translations;
        },

        /**
         * Save global settings.
         */
        SET_GLOBAL_SETTINGS(state, v) {
            state.globalSettings = v;
        },

        /**
         * Switches a controller that is displayed in the image view.
         */
        SET_VIEW_CONTROLLER(state, controller) {
            if (controller !== state.viewController) {
                // // Disconnect all event listeners from the old controller.
                // if (state.viewController) {
                //     state.viewController.off('.store');
                // }

                state.viewController = controller;
            }
        },

        /**
         * Switches the main controller.
         */
        SET_MAIN_CONTROLLER(state, controller) {
            if (controller !== state.mainController) {
                // Disconnect all event listeners from the old controller.
                if (state.mainController) {
                    state.mainController.off('.store');
                }

                state.mainController = controller;
            }
        },

        /**
         * Saves variants.
         */
        SET_VARIANTS(state, v) {
            state.variants = v;
        },

        /**
         * Saves current group.
         */
        SET_CURRENT_GROUP(state, v) {
            state.currentGroup = v;
        },

        /**
         * Add seen group id.
         */
        ADD_SEEN_GROUP(state, code) {
            if (!state.seenGroups.includes(code)) {
                state.seenGroups.push(code);
            }

            updateSeenAndAlertGroups(state);
        },

        REMOVE_SEEN_GROUP(state, code) {
            state.seenGroups = state.seenGroups.filter((x) => x !== code);

            updateSeenAndAlertGroups(state);
        },

        SET_SEEN_GROUPS(state, v) {
            state.seenGroups = v;

            updateSeenAndAlertGroups(state);
        },

        ADD_ALERT_GROUP(state, code) {
            if (!state.alertGroups.includes(code)) {
                state.alertGroups.push(code);
            }

            updateSeenAndAlertGroups(state);
        },

        REMOVE_ALERT_GROUP(state, code) {
            state.alertGroups = state.alertGroups.filter((x) => x !== code);

            updateSeenAndAlertGroups(state);
        },

        SET_ALERT_GROUPS(state, v) {
            state.alertGroups = v;

            updateSeenAndAlertGroups(state);
        },

        SET_BOX_NAME(state, v) {
            if (v?.controller) {
                state.boxName = v.text;

                state.boxNamesByController[v.controller.id] = v.text;
            } else {
                state.boxName = v;

                updateBoxNames(state);
            }
        },

        SET_CONTACT_NAME(state, v) {
            state.contactName = v;
        },

        SET_CONTACT_PHONE(state, v) {
            state.contactPhone = v;
        },

        SET_MAIN_ICON(state, v) {
            state.mainIcon = v;
        },

        SET_CART(state, v) {
            state.cart = v;
        },

        SET_SAVED_TICKETS(state, v) {
            state.savedTickets = v;
        },

        BUMP_VERSION(state) {
            state.configurationVersion += 1;
        },

        SET_RECIPE_OVERRIDE(state, v) {
            state.recipeOverride = v;
        },

        SET_ROTATION(state, v) {
            state.rotation = v;
        },

        SET_PREVIEW_ROTATION(state, v) {
            state.previewRotation = v;
        },

        SET_IS_MOBILE(state, v) {
            state.isMobile = v;
        },

        SET_IS_MOBILE_APP(state, v) {
            state.isMobileApp = v;
        },

        SET_IS_SUB_MENU_OPEN(state, v) {
            state.isSubMenuOpen = v;
        },

        SET_IS_NAV_MENU_OPEN(state, v) {
            state.isNavMenuOpen = v;
        },

        SET_DID_NAVIGATE_FROM_MAIN_MENU(state, v) {
            state.didNavigateFromMainMenu = v;
        },

        SET_IS_KEYBOARD_OPEN(state, v) {
            state.isKeyboardOpen = v;
        },

        SET_SELECTED_SWATCH_COLORS(state, v) {
            state.selectedSwatchColors[state.currentGroup.name] = v;

            updateSelectedSwatchColors(state);
        },

        SET_PREVIEW_IMAGES(state, v) {
            state.popPreviewImage = v.popPreviewImage;
            state.boxPreviewImage = v.boxPreviewImage;
        },

        SET_ATTACH_PACKS_PREVIEW_IMAGE(state, v) {
            state.attachPacksPreviewImage = v;
        },

        SET_PREVIEW_IMAGE_BY_CONTROLLER(state, v) {
            state.popPreviewImageByController[state.viewController.id] = v;
        },

        SET_CART_IMAGE_BY_CONTROLLER(state, v) {
            state.popCartImageByController[state.viewController.id] = v;
        },

        SET_CART_IMAGE_BY_SPECIFIC_CONTROLLER(state, payload) {
            // ^^^^ the setter above reuses the same controller for "two pack" so it's better not to contaminate that one.
            state.popCartImageBySpecificController[payload.controller.id] = payload.image;
        },

        SET_DIRTY_BY_CONTROLLER(state, controller) {
            state.dirtyByController[controller?.id ?? state.viewController.id] = true;
        },

        SET_PETS_BY_CONTROLLER(state, { controller, pets }) {
            state.petsByController[controller.id] = pets;
        },

        SET_ATTACH_PACKS_BY_CONTROLLER(state, { controller, attachPacks }) {
            state.attachPacksByController[controller.id] = attachPacks;
        },

        SET_LAST_PRINT_RECIPE(state, v) {
            state.lastPrintRecipe = v;
        },

        SET_PRINT_ATTEMPTED(state, v) {
            state.printAttempted = v;
        },

        SET_PRINT_ATTEMPTED_AT_VERSION(state, v) {
            state.printAttemptedAtVersion = v;
        },

        SET_NO_SAVE_PREVIEWS(state, v) {
            state.noSavePreviews = v;
        },

        SET_RECOMMENDATIONS(state, v) {
            state.recommendations = v;
        },

        SET_SELECTED_RECOMMENDATIONS(state, v) {
            state.selectedRecommendations = v;
        },

        SET_SAVED_RECIPE(state, v) {
            state.savedRecipe = v;
        },

        SET_AI_PREVIEW(state, v) {
            state.aiPreview = v;
        },

        SET_AI_SELECTIONS(state, v) {
            state.aiSelections = v;
        },

        SET_AI_MATCH_ERROR(state, v) {
            state.aiMatchError = v;
        },

        SET_LAST_SAVE_RESULT(state, v) {
            state.lastSaveResult = v;
        },

        SET_DEFAULT_BOX(state, v) {
            this.defaultBox = v;
        },
    },

    actions: {
        /**
         * Restart Customizer
         */
        restartCustomizer({ commit }) {
            commit('RESTART');
        },

        /**
         * Saves options.
         */
        setCustomizerOptions({ commit }, options) {
            if (!Array.isArray(options.products)) {
                options.products = [options.products];
            }

            options.products = options.products.slice(0, 1);

            commit('SET_CUSTOMIZER_OPTIONS', options);
        },

        /**
         * Inject translations.
         */
        setTranslations({ commit }, translations) {
            commit('SET_TRANSLATIONS', translations);
        },

        /**
         * Inject translations.
         */
        setTranslationsRaw({ commit }, translations) {
            commit('SET_TRANSLATIONS_RAW', translations);
        },

        /**
         * Load a special configuration product.
         */
        loadSettings({
            state,
            commit,
            dispatch,
            rootState,
        }) {
            const controller = new state.options.DriveCustomizer.Controller(
                { ...state.defaultControllerOptions, ...{ products: 'POPPeopleSettings' } },
            );

            controller.on('initializeAvailability.store', () => new Promise((resolve) => {
                const tasks = [
                    dispatch('getPrices', {
                        controller,
                    }).then((data) => {
                        dispatch('log', {
                            level: 'info',
                            message: 'Initialize availability...',
                        });

                        return data;
                    }),
                ];

                Promise.all(tasks).then(() => {
                    resolve();
                });
            }));

            controller.on('getInventory.store', (c) => {
                const v = getAts(rootState, c, controller);

                return v;
            });

            controller.on('afterAssembliesBuilt', () => {
                commit('SET_GLOBAL_SETTINGS', controller);

                dispatch('updateTranslations', controller);
            });

            controller.load();
        },

        /**
         * Load recommendations.
         */
        getRecommendations({ commit }) {
            return new Promise((resolve) => {
                const container = document.querySelector('.py-recommendations-json-container');

                if (container) {
                    const text = container.textContent?.trim();

                    if (text) {
                        try {
                            const json = JSON.parse(text);

                            commit('SET_RECOMMENDATIONS', json);
                        } catch (e) {
                            // Nothing.
                        }
                    }
                }

                resolve();
            });
        },

        /**
         * Begins Loading the customizer.
         */
        load({ dispatch }, { mode }) {
            dispatch('loadState', 'loading');

            dispatch('loadCustomizeFlow', { mode });
        },

        /**
         * Continue loading the main controller.
         */
        loadCustomizeFlowContinue({ state, commit, dispatch }, { startWithProducts, recipe }) {
            let loadProducts = startWithProducts;

            if (recipe) {
                // eslint-disable-next-line
                console.log(`Switching initial loading from ${loadProducts} to ${recipe.styleCode}`);

                loadProducts = [recipe.styleCode];

                if (recipe.custom['fun-two-pack']) {
                    // Nevermind, it is a two pack.
                    // TODO: Temporary hardcoded before SFCC catches up.
                    loadProducts = ['POPPeopleTwoPackWebBase~POPLeft,POPPeopleWebBase~POPRight,POPPeopleWebBase'];

                    // eslint-disable-next-line
                    console.log(`Switching initial loading to ${loadProducts}`);
                }
            }

            const controller = new state.options.DriveCustomizer.Controller(
                { ...state.defaultControllerOptions, ...{ products: loadProducts } },
            );

            controller.on('trackAction', () => {
                // console.log('Action! ', action);
            });

            controller.on('pickAlternate', (placement) => {
                let alternate = null;

                // Port functionality from v4.
                if (alternate == null) {
                    // Find the first available in the placement that was preferred.
                    alternate = placement.components.find((c) => !c.placeholder && !c.fallback && c.preferred && controller.isAvailable(c));
                }

                if (alternate == null) {
                    // Find the first available placeholder in the placement that was preferred.
                    alternate = placement.components.find((c) => c.placeholder && !c.fallback && c.preferred && controller.isAvailable(c));
                }

                if (alternate == null) {
                    alternate = placement.components.find((c) => !c.fallback && controller.isAvailable(c));
                }

                if (alternate == null) {
                    alternate = placement.components.find((c) => c.fallback && c.preferred && controller.isAvailable(c));
                }

                if (alternate == null) {
                    alternate = placement.components.find((c) => c.fallback && controller.isAvailable(c));
                }

                return alternate;
            });

            // controller.traceAllowed = true;
            // controller.traceAvailable = true;
            // controller.traceUpdated = true;

            // eslint-disable-next-line no-shadow
            const restoreController = (controller, recipe, restoreName) => {
                commit('SET_VIEW_CONTROLLER', controller);

                // eslint-disable-next-line
                console.log('Restoring the state from a recipe');

                const availabilityWarning = [];
                let seenGroups = [];

                const skipGroups = [];

                let canRestoreHeads = true;

                const restoreComponents = recipe.components;

                // Swap heads and hair so loading from recipes can resolve rules better.
                const headIndex = restoreComponents.findIndex((x) => x.code === 'POPHead');
                const hairIndex = restoreComponents.findIndex((x) => x.code === 'POPHair');

                if (headIndex >= 0 && hairIndex >= 0) {
                    const head = restoreComponents[headIndex];
                    restoreComponents[headIndex] = restoreComponents[hairIndex];
                    restoreComponents[hairIndex] = head;
                }

                restoreComponents.forEach((component) => {
                    // Bail early if it's not worth checking anymore.
                    if (!canRestoreHeads) {
                        return;
                    }

                    // Can we even restore heads?
                    if (component.code === 'POPHead' && component.component.custom?.['fun-type'] === 'head') {
                        const placement = controller.findPlacement(component.code);
                        if (placement && placement.components) {
                            let match = placement.components.find((c) => c.code === component.component.code);

                            if (match == null) {
                                // Is it a beard SKU?
                                const sku = component.component.custom['fun-sku'];

                                const notBeard = placement.components.find((c) => c.custom['fun-sku-beards'] === sku);

                                if (notBeard) {
                                    match = notBeard;
                                }
                            }

                            if (match) {
                                // Are there any heads in stock for this color?
                                const color = match.custom['fun-skin-color'];

                                let anyHeads = false;

                                placement.components.forEach((c) => {
                                    if (c.custom['fun-skin-color'] === color) {
                                        if (controller.isAvailable(c)) {
                                            anyHeads = true;
                                        }
                                    }
                                });

                                if (!anyHeads) {
                                    canRestoreHeads = false;
                                }
                            } else {
                                canRestoreHeads = false;
                            }
                        }

                        if (!canRestoreHeads) {
                            return;
                        }
                    }

                    if (skipGroups.includes(component.code)) {
                        return;
                    }

                    if (component.component) {
                        // Find matching component in the blueprint, otherwise isAvailable doesn't work
                        // since recipes don't include availability information.
                        const placement = controller.findPlacement(component.code);

                        // Skip placements if tagged as such to skip when restoring from recipes.
                        if (placement?.custom?.['fun-skip-from-recipe'] === 'true') {
                            return;
                        }

                        if (placement && placement.components) {
                            let match = placement.components.find((c) => c.code === component.component.code);

                            if (match == null) {
                                // Is it a beard SKU?
                                const sku = component.component.custom['fun-sku'];

                                const notBeard = placement.components.find((c) => c.custom['fun-sku-beards'] === sku);

                                if (notBeard) {
                                    const beardComponent = controller.findPlacement(constants.PLACEMENT_OTHER).components.find((c) => {
                                        if (c?.custom['fun-sku'] === sku) {
                                            return true;
                                        }

                                        return false;
                                    });

                                    // Is the beard SKU out of stock?
                                    if (beardComponent == null || !controller.isAvailable(beardComponent)) {
                                        // eslint-disable-next-line max-len, no-console
                                        console.log(`%cCould not restore ${component.component.code} with beard ${beardComponent?.code} from recipe (#4)`, 'color: #b00b00;');

                                        // Skip beards.
                                        skipGroups.push('POPBeard');

                                        // Flag head.
                                        let group = null;

                                        controller.blueprint.groups.forEach((g) => {
                                            if (g.placements && g.placements.some((p) => p.code === component.code)) {
                                                group = g.code;
                                            }
                                        });

                                        if (group) {
                                            availabilityWarning.push(group);
                                        }
                                    }

                                    match = notBeard;
                                }
                            }

                            if (match) {
                                if (!match.placeholder) {
                                    // Use original component quantity.
                                    const quantity = component.component.quantityMultiplier != null
                                        ? component.component.quantityMultiplier
                                        : 1;

                                    // Find a group that this placement belongs to.
                                    let group = null;

                                    controller.blueprint.groups.forEach((g) => {
                                        if (g.placements && g.placements.some((p) => p.code === component.code)) {
                                            group = g.code;
                                        }
                                    });

                                    if (controller.isAvailable(match, quantity)) {
                                        if (!seenGroups.includes(group)) {
                                            seenGroups.push(group);
                                        }

                                        controller.updateComponent(component.code, match.code);

                                        const selected = controller.selectedAtPlacement(component.code);
                                        if (selected) {
                                            selected.quantityMultiplier = quantity;
                                        }
                                    } else {
                                        // eslint-disable-next-line no-console
                                        console.log(`%cCould not restore ${component.component.code} from recipe (#2)`, 'color: #b00b00;');

                                        if (!seenGroups.includes(group)) {
                                            seenGroups.push(group);
                                        }

                                        availabilityWarning.push(group);
                                    }
                                }
                            } else {
                                // Previously saved component is no longer valid.
                                // Throw a warning for the group.
                                // eslint-disable-next-line no-console
                                console.log(`%cCould not restore ${component.component.code} from recipe (#3)`, 'color: #b00b00;');

                                // Did it have a SKU?
                                if (component.component.custom?.['fun-sku']) {
                                    // eslint-disable-next-line no-console
                                    console.log(`... and it had a SKU ${component.component.custom?.['fun-sku']}`);

                                    // Find a group that this placement belongs to.
                                    let group = null;
                                    let loadPlacement = null;

                                    controller.blueprint.groups.forEach((g) => {
                                        if (g.placements && g.placements.some((p) => p.code === component.code)) {
                                            group = g.code;
                                            loadPlacement = g.placements.find((p) => p.code === component.code);
                                        }
                                    });

                                    if (!seenGroups.includes(group)) {
                                        seenGroups.push(group);
                                    }

                                    if (loadPlacement?.custom?.['fun-no-oos-warning'] !== 'true') {
                                        availabilityWarning.push(group);
                                    }
                                }
                            }
                        }
                    }
                });

                if (!canRestoreHeads) {
                    // Unselect body and color.
                    const bodyPlacement = controller.findPlacement(constants.PLACEMENT_BODY);

                    if (bodyPlacement) {
                        const bodyPlaceholder = bodyPlacement.components.find((c) => c.placeholder);

                        if (bodyPlaceholder) {
                            controller.updateComponent(constants.PLACEMENT_BODY, bodyPlaceholder.code);
                        }
                    }

                    const colorPlacement = controller.findPlacement(constants.PLACEMENT_SKIN_COLOR);

                    if (colorPlacement) {
                        const colorPlaceholder = colorPlacement.components.find((c) => c.placeholder);

                        if (colorPlaceholder) {
                            controller.updateComponent(constants.PLACEMENT_SKIN_COLOR, colorPlaceholder.code);
                        }
                    }

                    availabilityWarning.push('Body');

                    dispatch('initializationWarning');
                }

                // Count pets and reset if necessary.
                let petsCount = 0;

                controller.state.forEach((s) => {
                    if (constants.PLACEMENT_PETS.includes(s.placement.code) && !s.component.placeholder) {
                        petsCount += s.component.quantityMultiplier || 1;
                    }
                });

                if (petsCount > constants.PETS_LIMIT) {
                    [...controller.state].forEach((s) => {
                        if (constants.PLACEMENT_PETS.includes(s.placement.code) && !s.component.placeholder) {
                            const placeholder = s.placement.components.find((x) => x.placeholder);

                            controller.updateComponent(s.placement.code, placeholder.code);
                        }
                    });

                    // Tag group.
                    if (!availabilityWarning.includes('Pets')) {
                        availabilityWarning.push('Pets');
                    }
                }

                // Count attach packs and reset if necessary.
                let apsCount = 0;

                controller.state.forEach((s) => {
                    if (constants.PLACEMENT_PACKS.includes(s.placement.code) && !s.component.placeholder) {
                        apsCount += s.component.quantityMultiplier || 1;
                    }
                });

                if (apsCount > constants.ATTACH_PACKS_LIMIT) {
                    [...controller.state].forEach((s) => {
                        if (constants.PLACEMENT_PACKS.includes(s.placement.code) && !s.component.placeholder) {
                            const placeholder = s.placement.components.find((x) => x.placeholder);

                            controller.updateComponent(s.placement.code, placeholder.code);
                        }
                    });

                    // Tag group.
                    if (!availabilityWarning.includes('Attach Packs')) {
                        availabilityWarning.push('Attach Packs');
                    }
                }

                // Name and Review steps/groups should always be "seen" when restoring from a recipe.
                const validatedGroups = [];

                controller.blueprint.groups.forEach((g) => {
                    if (seenGroups.includes(g.code)) {
                        validatedGroups.push(g.code);
                    } else if (g.custom?.['fun-show-in-recipe'] === 'true') {
                        let anyActive = false;

                        if (g.placements) {
                            // eslint-disable-next-line no-restricted-syntax
                            for (const p of g.placements) {
                                if (p.components) {
                                    // eslint-disable-next-line no-restricted-syntax
                                    for (const otherComponent of p.components) {
                                        if (!otherComponent.placeholder && controller.isAvailable(otherComponent)) {
                                            anyActive = true;
                                        }
                                    }
                                }
                            }
                        }

                        if (anyActive) {
                            validatedGroups.push(g.code);
                        }
                    }
                });

                seenGroups = validatedGroups;

                // Restore box name.
                if (restoreName && recipe.custom?.['fun-name']) {
                    dispatch('updateBoxName', {
                        name: recipe.custom?.['fun-name'],
                        controller,
                    });
                }

                // Populate seen groups in navigation.
                seenGroups.forEach((g) => {
                    commit('ADD_SEEN_GROUP', g);
                });

                // Populate alerted groups.
                availabilityWarning.forEach((g) => {
                    commit('ADD_ALERT_GROUP', g);
                });

                if (availabilityWarning.length > 0) {
                    dispatch('initializationWarning');
                }

                // When returning from a recipe, assume it is dirty.
                commit('SET_DIRTY_BY_CONTROLLER', controller);
            };

            controller.on('afterAssembliesBuilt.store', () => {
                if (recipe) {
                    // Restore main controller.
                    restoreController(controller, recipe, recipe.assembly == null || recipe.assembly.length === 0);

                    if (recipe.assembly) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const nested of recipe.assembly) {
                            // Find matching controller.
                            const sockets = controller.allSockets();

                            const match = sockets.find((x) => x.socket.code === nested.socket);

                            if (match.to) {
                                restoreController(match.to, nested.recipe, true);
                            }
                        }
                    }
                }

                // Load actual product options.
                dispatch('loadColors', {
                    controllers: [controller],
                    recipe,
                });
            });

            controller.on('load-error.store', () => {
                dispatch('loadState', 'error');
            });

            // Load controller.
            dispatch('loadController', controller);
        },

        /**
         * Loads customization flow.
         */
        loadCustomizeFlow({ dispatch, state }, { mode }) {
            const uri = new Uri(window.location.href);

            const recipeId = state.options.recipe;

            const continueStandard = () => {
                let startWithProducts = state.options.products;

                if (mode === 'two-pack') {
                    startWithProducts = state.options.productsTwoPack;

                    // TODO: Temporary hardcoded before SFCC catches up.
                    if (startWithProducts == null) {
                        startWithProducts = 'POPPeopleTwoPackWebBase~POPLeft,POPPeopleWebBase~POPRight,POPPeopleWebBase';
                    }
                }

                dispatch('loadCustomizeFlowContinue', {
                    startWithProducts,
                });
            };

            if (recipeId && recipeId.length > 0 && recipeId !== 'null') {
                uri.deleteQueryParam('recipe');

                window.history.replaceState(null, '', uri.toString());

                axios.get(`https://cz.drrv.co/recipe/${recipeId}.json`).then((recipeData) => {
                    const recipe = recipeData.data;

                    dispatch('loadCustomizeFlowContinue', {
                        // TODO: Determine if the recipe was two pack or single pack...
                        startWithProducts: state.options.products,
                        recipe,
                    });

                    dispatch('setSavedRecipe', { recipe });
                }).catch(() => {
                    // Regular load since the recipe is not available.
                    state.options.recipe = null;

                    continueStandard();
                });
            } else {
                continueStandard();
            }
        },

        /**
         * Show a warning if a recipe could not be loaded completely.
         */
        initializationWarning({ dispatch }) {
            dispatch('showOutOfStockWarning', true);
        },

        /**
         * Update translations for the current locale from the controller.
         */
        updateTranslations({ state }, controller) {
            if (controller.blueprint?.translations) {
                const locale = state.options.locale || 'en';

                let messages = state.translationsRaw.getLocaleMessage(locale);

                if (messages == null) {
                    messages = {};
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const k of Object.keys(controller.blueprint.translations)) {
                    messages[k] = controller.blueprint.translations[k];
                }

                state.translationsRaw.setLocaleMessage(locale, messages);
            }
        },

        /*
         * Loads product options.
         */
        loadColors({ state, rootState, dispatch }, { controllers, recipe }) {
            let showController = null;

            if (controllers.length > 0) {
                // eslint-disable-next-line
                showController = controllers[0];
            }

            if (showController) {
                dispatch('updateTranslations', showController);

                // Configure the initial site.
                if (state.options && state.options.configuration.site) {
                    dispatch('log', {
                        level: 'info',
                        message: `Starting with site ${state.options.configuration.site}`,
                    });

                    let tag;

                    showController.forAllControllers((controller) => {
                        const sitePlacement = controller.findPlacement(constants.PLACEMENT_SITE);

                        if (sitePlacement) {
                            const site = sitePlacement.components.find((c) => c.placementIndependentCode === state.options.configuration.site);
                            if (site) {
                                tag = site.tags.find((t) => t.startsWith('site-'));

                                dispatch('log', {
                                    level: 'info',
                                    message: `Selecting site ${site.code} with tags ${tag}`,
                                });

                                controller.updateComponent(constants.PLACEMENT_SITE, site.code);
                            } else {
                                dispatch('log', {
                                    level: 'info',
                                    message: 'Could not find a site component',
                                });
                            }
                        }
                    });

                    // Select a box manually since we don't want to mess up the editor.
                    // Only on parent controller.
                    const boxPlacement = showController.findPlacement(constants.PLACEMENT_BOX);

                    if (boxPlacement && tag) {
                        const box = boxPlacement.components.find((c) => c.tags && c.tags.includes(tag) && showController.isAvailable(c));

                        if (box) {
                            dispatch('log', {
                                level: 'info',
                                message: `Selecting box ${box.code}`,
                            });

                            showController.updateComponent(constants.PLACEMENT_BOX, box.code);
                        }
                    }
                }

                if (window.dataLayer && !state.options.skipLanding) {
                    window.dataLayer.push({
                        event: 'pop_people_start',
                    });
                }

                // Go to either Looks Good step or the first alerted group.
                // let startWithGroup = null;

                // // if (availabilityWarning.length > 0) {
                // //     // Find the first group that is flagged.
                // //     startWithGroup = controller.blueprint.groups.find((g) => availabilityWarning.includes(g.code));
                // // }

                // if (startWithGroup == null) {
                //     startWithGroup = controller.blueprint.groups.find((g) => g.code === constants.GROUP_REVIEW);
                // }

                // if (startWithGroup) {
                //     // Delay initialization, otherwise the group selection will be overwritten.
                //     setTimeout(() => {
                //         dispatch('showGroup', startWithGroup);
                //     });
                // }

                let usedAlert = false;

                // eslint-disable-next-line no-restricted-syntax
                for (const controllerId of Object.keys(state.alertGroupsByController)) {
                    let controller = null;

                    showController.forAllControllers((c) => {
                        if (c.id === controllerId) {
                            controller = c;
                        }
                    });

                    const alerted = state.alertGroupsByController[controllerId];

                    if (alerted && alerted.length > 0) {
                        let startWithGroup = controller.blueprint.groups.find((g) => alerted.includes(g.code));

                        if (startWithGroup == null) {
                            startWithGroup = controller.blueprint.groups.find((g) => g.custom && g.custom['fun-start-group'] === 'true');
                        }

                        dispatch('showGroup', startWithGroup);

                        dispatch('showController', {
                            main: showController,
                            view: controller,
                        });

                        usedAlert = true;
                        break;
                    }
                }

                // Configure a global tag for ecomm vs retail targeting.
                if (state.options.site?.includes('ecommerce')) {
                    showController.forAllControllers((c) => {
                        c.addTag('ecommerce');
                    });
                } else {
                    showController.forAllControllers((c) => {
                        c.addTag('retail');
                    });
                }

                // Force validation to reestablish boxes, blisters, etc.
                showController.validateAllComponents();

                if (!usedAlert) {
                    if (showController.blueprint.custom['fun-two-pack'] === 'true') {
                        // If coming from a recipe, and there were no alerts... go to review _for the second pop_.
                        if (recipe) {
                            // Select the first pop and show that.
                            let lastPop = null;

                            showController.forAllControllers((c) => {
                                if (c !== showController) {
                                    lastPop = c;
                                }
                            });

                            const startGroup = lastPop.blueprint.groups.find((g) => g.code === constants.GROUP_NAME);

                            dispatch('showGroup', startGroup);

                            dispatch('showController', {
                                main: showController,
                                view: lastPop,
                            });
                        } else {
                            // Select the first pop and show that.
                            let firstPop = null;

                            showController.forAllControllers((c) => {
                                if (c !== showController && firstPop == null) {
                                    firstPop = c;
                                }
                            });

                            const startGroup = firstPop.blueprint.groups.find((g) => g.custom && g.custom['fun-start-group'] === 'true');

                            dispatch('showGroup', startGroup);

                            dispatch('showController', {
                                main: showController,
                                view: firstPop,
                            });
                        }
                    } else {
                        let startGroup = showController.blueprint.groups.find((g) => g.custom && g.custom['fun-start-group'] === 'true');

                        if (recipe) {
                            startGroup = showController.blueprint.groups.find((g) => g.code === constants.GROUP_NAME);
                        }

                        dispatch('showGroup', startGroup);

                        dispatch('showController', {
                            main: showController,
                            view: showController,
                        });
                    }
                }

                if (rootState.deferAiTrigger) {
                    dispatch('showAiModal', true);
                }

                // dispatch('checkStashedAiSelections');

                dispatch('syncPets');
                dispatch('syncAttachPacks');

                dispatch('loadState', 'loaded');
            }

            dispatch('globalBusy', false);
        },

        /**
         * Send current configuration to Google.
         */
        trackStart({ state, getters }) {
            if (state.options.isKiosk) {
                return;
            }

            // Hard-coded version.

            // Notify about the Get Started click.
            if (window.dataLayer == null) {
                window.dataLayer = [];
            }

            if (window.dataLayer) {
                window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.

                // Find what is select in the box.
                window.dataLayer.push({
                    event: 'product_view',
                    ecommerce: {
                        currencyCode: 'USD', // use the correct currency code value here
                        detail: {
                            actionField: {
                                action: 'detail',
                            },
                            products: [{
                                name: 'Pop! Yourself', // Set to this value
                                id: getters.isTwoPack ? '84993' : '83353',
                                price: '35.00',
                                brand: 'funko', // funko or loungefly
                                category: 'Pop! Yourself', // populate with the fandom
                                dimension1: 'pop people', // populate with the product page template
                                dimension2: 'list', // populate with the price type (sale, range, list)
                                dimension3: '', // populate with the product flag (New, Coming Soon, Sale, Exclusive, Preorder)
                                dimension4: '', // populate with the product badges, pipe delimit multiple values
                                dimension5: 'Pop! Yourself', // populate with the product license
                                dimension6: 'Pop! Yourself', // populate with the product sub license
                                dimension7: 'in stock', // populate with "in stock" or "out of stock"
                                dimension8: 'Pop! Yourself', // populate with the product type
                                dimension9: 'BAP Core 2023', // populate with the product series
                                dimension10: 'Pop! Yourself', // populate with the product sub type
                                dimension11: 'Pop! Yourself', // populate with the product character
                                dimension12: 'no', // populate with "yes" or "no" on if the product is high profile
                            }],
                        },
                    },
                });
            }
        },

        /**
         * Send current configuration to Google.
         */
        trackAddToCart() {
            // Don't track anymore. At least for now.
        },

        /**
         * Displays the controller in the render view.
         */
        showController({
            commit, dispatch, rootState,
        }, controllers) {
            const main = controllers?.main;
            const view = controllers?.view;

            commit('SET_MAIN_CONTROLLER', main);
            commit('SET_VIEW_CONTROLLER', view);

            if (main) {
                main.off('.price');

                main.on('error.price', (e) => {
                    // eslint-disable-next-line
                    console.log(JSON.stringify(e));
                });

                // Listen to controller level events.
                // We wouldn't need to turn off listeners when an active controller changes since
                // previous controllers would be disabled automatically.
                main.on('stateChanged.price', () => {
                    dispatch('updatePriceDebounce', main);
                });

                // SPELLING BEE START
                if (rootState.isSpellingBee) {
                    main.forAllControllers((c) => {
                        c.addTag('spelling-bee-enabled');
                    });
                }
                // SPELLING BEE END

                dispatch('updatePrice', main);
            }
        },

        /**
         * Selects a group to display.
         */
        showGroup({ commit }, group) {
            if (group) {
                commit('ADD_SEEN_GROUP', group.code);
            }

            commit('SET_CURRENT_GROUP', group);
        },

        /**
         * Adds a group the list of alert groups.
         */
        addAlertGroup({ commit }, group) {
            commit('ADD_ALERT_GROUP', group.code);
        },

        /**
         * Add a group to seen list.
         */
        addSeenGroup({ commit }, code) {
            commit('ADD_SEEN_GROUP', code);
        },

        /**
         * Remove a group from seen list.
         */
        unseeGroup({ commit }, code) {
            commit('REMOVE_SEEN_GROUP', code);
        },

        /**
         * Removes a group the list of alert groups.
         */
        removeAlertGroup({ commit }, group) {
            commit('REMOVE_ALERT_GROUP', group.code);
        },

        /**
         * Debounced price update.
         */
        updatePriceDebounce({ dispatch }, controller) {
            if (updatePriceTimer) {
                clearTimeout(updatePriceTimer);
            }

            updatePriceTimer = setTimeout(() => {
                dispatch('updatePrice', controller);
            });
        },

        /**
         * Updates current pricing with controller information.
         */
        updatePrice({
            dispatch,
        }, controller) {
            if (controller == null) {
                return;
            }

            // Calculate the current product price.
            dispatch('getStatePrice', {
                controller,
            }).then(() => {
                // TODO: Extract prices.
            });
        },

        /**
         * Save recipe for Funko.
         */
        saveRecipe({ dispatch }, { type }) {
            dispatch('customizerDone', {
                type,
            });
        },

        /**
         * Save recommendations to cart.
         */
        saveRecommendations({ state, dispatch }, options) {
            const getImage = (product) => {
                let image = product?.images?.small[0]?.url;

                if (image == null) {
                    image = product?.images?.medium[0]?.url;
                }

                if (image == null) {
                    image = product.image;
                }

                return image;
            };

            // eslint-disable-next-line no-async-promise-executor
            return new Promise(async (resolve) => {
                if (options?.saveTemporary) {
                    resolve();
                    return;
                }

                if (state.selectedRecommendations) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const product of state.selectedRecommendations) {
                        for (let i = 1; i < 20; i += 1) {
                            // Find empty placement.
                            const placementCode = `${constants.PLACEMENT_PRODUCTS}${i}`;
                            const selected = state.mainController.selectedAtPlacement(placementCode);

                            if (selected?.placeholder) {
                                const placement = state.mainController.findPlacement(placementCode);
                                // Use this one.
                                const realProduct = placement.components.find((x) => !x.placeholder);

                                state.mainController.updateComponent(placementCode, realProduct.code);

                                let price = null;

                                if (product.price?.sales?.value) {
                                    price = product.price.sales.value;
                                }

                                if (price == null && product.price) {
                                    price = +product.price;
                                }

                                const image = getImage(product);

                                state.mainController.commitCustomAttributes(placementCode, {
                                    'fun-sku': product.id,
                                    'fun-name': product.name,
                                    'fun-price': price ? price.toString() : '',
                                    'fun-image': image,
                                });

                                dispatch('setComponentQuantity', {
                                    component: realProduct,
                                    quantity: product.quantity ?? 1,
                                });

                                break;
                            }
                        }

                        // Instead of manipulating cart, store recommendations on the main controller.

                        // if (product.itemUuid) {
                        //     // Update existing.
                        //     const baseUrl = state.globalSettings?.blueprint?.custom?.['fun-recommendations-update-endpoint'];

                        //     const params = {
                        //         pid: product.id,
                        //         quantity: product.quantity,
                        //         uuid: product.itemUuid,
                        //     };

                        //     try {
                        //         // eslint-disable-next-line no-await-in-loop
                        //         const response = await axios.get(baseUrl, {
                        //             params,
                        //             withCredentials: true,
                        //         });

                        //         console.log('Quantity updated successfully:', response.data);
                        //     } catch (error) {
                        //         console.error('Failed to update quantity:', error.response?.data || error.message);
                        //     }
                        // } else if (product.quantity > 0) {
                        //     // Add new.
                        //     const url = state.globalSettings?.blueprint?.custom?.['fun-recommendations-add-endpoint'];

                        //     const formData = new URLSearchParams();

                        //     formData.append('pid', product.id);
                        //     formData.append('quantity', product.quantity);
                        //     formData.append('options', '[]');

                        //     try {
                        //         // eslint-disable-next-line no-await-in-loop
                        //         const response = await axios.post(url, formData, {
                        //             headers: {
                        //                 'Content-Type': 'application/x-www-form-urlencoded',
                        //             },
                        //             withCredentials: true, // include cookies if session required
                        //         });

                        //         console.log('Add to cart successful:', response.data);
                        //     } catch (error) {
                        //         console.error('Add to cart failed:', error.response?.data || error.message);
                        //     }
                        // }
                    }
                }

                resolve();
            });
        },

        /**
         * Save recipe
         */
        customizerDone({
            dispatch,
            state,
            getters,
            rootState,
        }, options) {
            return new Promise((resolve, reject) => {
                function downloadRecipes(recipes) {
                    return new Promise((after) => {
                        const tasks = [];

                        recipes.forEach((recipe) => {
                            let l = recipe.location;
                            if (l.startsWith('//')) {
                                l = `https:${l}`;
                            }

                            tasks.push(axios.get(l).then((recipeResponse) => {
                                const { data } = recipeResponse;

                                recipe.views = data.views.map((v) => ({
                                    code: v.code,
                                    name: v.name,
                                    previewPng: v.previewPng,
                                    previewJpg: v.previewJpg,
                                    production: v.production,
                                }));
                            }));
                        });

                        Promise.all(tasks).then(() => {
                            after();
                        });
                    });
                }

                function downloadRecipesContent(recipes) {
                    return new Promise((after) => {
                        const tasks = [];

                        recipes.forEach((recipe) => {
                            let l = recipe.location;
                            if (l.startsWith('//')) {
                                l = `https:${l}`;
                            }

                            tasks.push(axios.get(l).then((recipeResponse) => {
                                const { data } = recipeResponse;

                                recipe.recipe = data;
                            }));
                        });

                        Promise.all(tasks).then(() => {
                            after();
                        });
                    });
                }

                function continueSaving(imageMap) {
                    const recipeProcessor = {
                        temporaryRecipe: options?.saveTemporary,
                        externalInventory: state.options.kioskInventoryLocation != null && state.options.kioskInventoryLocation.length > 0,

                        accepted() {
                        },

                        translateCode(componentCode) {
                            return componentCode;
                        },

                        translateRecipe(recipe, controller) {
                            // Pass over the client side preview.

                            if (recipe.views) {
                                recipe.views.forEach((view) => {
                                    if (view.code === 'Preview') {
                                        if (state.options.isKiosk) {
                                            view.clientRender = EMPTY_IMAGE;
                                            delete view.variants;
                                        } else {
                                            view.clientRender = imageMap[controller.id];

                                            if (options?.noVariants) {
                                                delete view.variants;
                                            }
                                        }
                                    }
                                });
                            }

                            // Save controller id.
                            recipe.custom['fun-controller-id'] = controller.id;

                            // Populate box SKU at the top level.

                            recipe.components.forEach((c) => {
                                if (c.code === 'POPBox') {
                                    recipe.custom['fun-sku'] = c.component.custom['fun-sku'];
                                }
                            });

                            // Name.

                            recipe.components.forEach((c) => {
                                if (c.component.custom && c.component.custom['personalization-text']) {
                                    recipe.custom['fun-name'] = c.component.custom['personalization-text'];
                                }
                            });

                            // SPELLING BEE START
                            if (rootState.isSpellingBee) {
                                recipe.custom['fun-spelling-bee'] = 'true';
                            }
                            // SPELLING BEE END

                            // TODO: Beards.

                            const hasBeards = controller.state.some((c) => c.component.tags && c.component.tags.includes('beard'));

                            recipe.components.forEach((s) => {
                                if (!s.component.placeholder) {
                                    const sku = s.component.custom['fun-sku'];

                                    if (hasBeards) {
                                        const skuBeards = s.component.custom['fun-sku-beards'];
                                        const upcBeards = s.component.custom['fun-upc-beards'];

                                        if (skuBeards && upcBeards) {
                                            // eslint-disable-next-line
                                            console.log(`Beards detected, swapping ${sku} for ${skuBeards}`);

                                            s.component.custom['fun-sku'] = skuBeards;
                                            s.component.custom['fun-upc'] = upcBeards;

                                            const beardHead = controller.findPlacement(constants.PLACEMENT_OTHER).components.find((c) => {
                                                if (c.code.includes(skuBeards)) {
                                                    return true;
                                                }

                                                return false;
                                            });

                                            if (beardHead) {
                                                s.component.code = `${s.code}-${beardHead.placementIndependentCode}`;
                                                s.component.placementIndependentCode = beardHead.placementIndependentCode;
                                            }
                                        }
                                    }
                                }
                            });
                        },
                    };

                    const finalizeSave = (recipe) => {
                        dispatch('globalBusy', false);

                        downloadRecipes([recipe]).then(() => {
                            const result = {
                                usage: options.type,
                                type: 'custom',
                                recipe,
                            };

                            if (state.options.recipe) {
                                result.originalRecipe = state.options.recipe;
                                result.originalRecipeFrom = state.options.recipeFrom;
                            }

                            if (options && options.cancelAfter) {
                                dispatch('globalSaveCancel', result);

                                resolve(result);
                            } else if (options && options.saveOnlyKiosk) {
                                resolve({
                                    result,
                                    imageMap,
                                });
                            } else if (options && options.saveOnly) {
                                resolve(result);
                            } else if (options && options.saveTemporary) {
                                resolve(result);
                            } else {
                                dispatch('globalDone', result);

                                resolve(result);
                            }
                        });
                    };

                    if (getters.popControllers.length === 1) {
                        dispatch('saveRecommendations', options).then(() => {
                            // Old method.
                            state.viewController.saveRecipe(recipeProcessor).then((recipe) => {
                                finalizeSave(recipe);
                            }).catch((error) => {
                                reject(error);
                            });
                        });
                    } else {
                        // Save packs.

                        // Save individual pops.
                        const saveTasks = [];

                        getters.popControllers.forEach((controller) => {
                            saveTasks.push(controller.saveRecipe({
                                // Mark child recipes.
                                childRecipe: true,
                                ...recipeProcessor,
                            }));
                        });

                        Promise.all(saveTasks).then((recipes) => {
                            dispatch('saveRecommendations', options).then(() => {
                                downloadRecipesContent(recipes).then(() => {
                                    state.mainController.saveRecipe({
                                        temporaryRecipe: options?.saveTemporary,

                                        translateRecipe: (recipe) => {
                                            // Pass over the client side preview.

                                            if (recipe.views) {
                                                recipe.views.forEach((view) => {
                                                    if (view.code === 'Preview') {
                                                        if (state.options.isKiosk) {
                                                            view.clientRender = EMPTY_IMAGE;
                                                            delete view.variants;
                                                        } else {
                                                            view.clientRender = imageMap[state.mainController.id];

                                                            if (options?.noVariants) {
                                                                delete view.variants;
                                                            }
                                                        }
                                                    }
                                                });
                                            }

                                            // Populate box SKU at the top level.

                                            recipe.components.forEach((c) => {
                                                if (c.code === 'POPBox') {
                                                    recipe.custom['fun-sku'] = c.component.custom['fun-sku'];
                                                }

                                                if (c.code === 'POPBoxPack') {
                                                    recipe.custom['fun-sku'] = c.component.custom['fun-sku'];
                                                }
                                            });

                                            // SPELLING BEE START
                                            if (rootState.isSpellingBee) {
                                                recipe.custom['fun-spelling-bee'] = 'true';
                                            }
                                            // SPELLING BEE END

                                            // Make assembly.
                                            recipe.assembly = [];

                                            state.mainController.allSockets().forEach((s) => {
                                                if (s.to) {
                                                    const index = getters.popControllers.indexOf(s.to);
                                                    // Forward the child secret ID.
                                                    const childSecret = recipes[index]?.secretId;
                                                    const childRecipe = recipes[index]?.recipe;

                                                    recipe.assembly.push({
                                                        component: s.component.code,
                                                        placement: s.placement.code,
                                                        socket: s.socket.code,
                                                        to: childRecipe.id,
                                                        toSecret: childSecret,
                                                        recipe: childRecipe,
                                                    });
                                                }
                                            });
                                        },
                                    }).then((recipe) => {
                                        finalizeSave(recipe);
                                    });
                                });
                            });
                        }).catch((error) => {
                            reject(error);
                        });
                    }
                }

                if (state.mainController) {
                    if (!options || !options.noBusy) {
                        dispatch('globalBusy', true);
                    }

                    const imageQueue = [];
                    const imageMap = {};
                    let needMain = true;

                    getters.popControllers.forEach((controller) => {
                        if (controller === state.mainController) {
                            needMain = false;
                        }

                        imageQueue.push({
                            controller,
                            onlyController: controller,
                            view: 'SnapshotPreview',
                        });
                    });

                    if (needMain) {
                        imageQueue.push({
                            controller: state.mainController,
                            view: 'SnapshotPreviewPack',
                        });
                    }

                    const renderImages = () => {
                        const next = imageQueue.shift();
                        if (next == null) {
                            continueSaving(imageMap);
                        } else {
                            emitter.$emit('snapshot', {
                                onlyController: next.onlyController,
                                view: next.view,
                                done: (imageData) => {
                                    imageMap[next.controller.id] = imageData;

                                    if (imageData) {
                                        dispatch('log', {
                                            level: 'info',
                                            message: `Saved data size ${imageData.length}`,
                                        });
                                    }

                                    if (imageData == null || imageData.length < MIN_IMAGE_SIZE) {
                                        dispatch('globalSaveError');
                                    } else {
                                        renderImages();
                                    }
                                },
                            });
                        }
                    };

                    renderImages();
                } else {
                    dispatch('globalCancel');

                    resolve();
                }
            });
        },

        globalSaveError({ dispatch }) {
            dispatch('showSaveError', true);
        },

        /**
         * Make a snapshot.
         */
        snapshot(_, toRender) {
            return new Promise((resolve) => {
                emitter.$emit('snapshot', {
                    view: toRender?.view ?? 'SnapshotPreview',
                    onlyController: toRender.onlyController,
                    done: (imageData) => {
                        resolve(imageData);
                    },
                });
            });
        },

        /**
         * Clears placement.
         */
        clearPlacement({ state }, options) {
            const controller = options.controller || state.viewController;
            const placement = options.controller ? options.placement : options;

            const placementCode = placement.placement.code;
            const selectedComponent = controller.selectedAtPlacement(placementCode);

            if (selectedComponent) {
                if (selectedComponent.custom['dynamic-image-url'] && selectedComponent.custom['dynamic-image-url'].length > 0) {
                    controller.updateDynamicImage(placementCode, {
                        title: '',
                        name: '',
                        url: '',
                        pngUrl: '',
                        jpgUrl: '',
                        width: '',
                        height: '',
                    });
                }

                if (selectedComponent.custom['personalization-text'] && selectedComponent.custom['personalization-text'].length > 0) {
                    controller.updatePersonalization(placementCode, '');
                }
            }

            controller.clearComponent(placementCode);
        },

        /**
         * Resets Customizations and revert selected step
         */
        resetCustomizations({
            state,
            commit,
            dispatch,
            getters,
        }) {
            if (state.mainController != null) {
                getters.allControllers.forEach((c) => {
                    const placements = c.state;

                    placements.forEach((placement) => {
                        const { code } = placement.placement;
                        if (code !== 'Product') {
                            dispatch('clearPlacement', placement);
                        }
                    });
                });
            }

            dispatch('showController', null);

            dispatch('showGroup', null);

            dispatch('selectStep', null);

            commit('RESET');
        },

        /**
         * Saves current view reference.
         */
        setCurrentView({ commit }, v) {
            commit('SET_CURRENT_VIEW', v);
        },

        /**
         * Begins customization process.
         */
        startCustomization({ dispatch, state }, { mode }) {
            dispatch('showController', null);

            // Reload the product data with fresh inventory.
            dispatch('load', {
                options: state.options,
                mode,
            });
        },

        /**
         * Updates box name _for a currently selected pop_.
         */
        updateBoxName({ commit, state, dispatch }, v) {
            let useController = state.viewController;
            let useName = v;

            if (v && v.name && v.controller) {
                useName = v.name;
                useController = v.controller;
            }

            dispatch('uiInteracted');

            return new Promise((resolve) => {
                if (useController) {
                    const boxName = useController.findPlacement(constants.PLACEMENT_BOX_NAME);

                    if (boxName) {
                        if (useName !== '') {
                            useController.updatePersonalization(boxName.code, useName).then((result) => {
                                if (result) {
                                    if (result.accepted && result.acceptedAs) {
                                        commit('SET_BOX_NAME', {
                                            controller: useController,
                                            text: result.acceptedAs,
                                        });

                                        resolve(result.acceptedAs);

                                        dispatch('syncTwoPackNames');
                                    } else if (result.lastValid != null) {
                                        resolve(result.lastValid);

                                        updateBoxNames(state);

                                        dispatch('syncTwoPackNames');
                                    }
                                }
                            });
                        } else {
                            const placements = state.viewController.state;
                            const boxNamePlacement = placements.find((placement) => placement.placement.code === constants.PLACEMENT_BOX_NAME);

                            this.dispatch('clearPlacement', boxNamePlacement);
                            commit('SET_BOX_NAME', {
                                controller: useController,
                                text: '',
                            });

                            dispatch('syncTwoPackNames');

                            resolve('');
                        }
                    }
                }
            });
        },

        /**
         * Updates contact name.
         */
        updateContactName({ commit }, v) {
            commit('SET_CONTACT_NAME', v);
        },

        /**
         * Updates contact phone.
         */
        updateContactPhone({ commit }, v) {
            commit('SET_CONTACT_PHONE', v);
        },

        /**
         * Make a print request.
         */
        async print({
            dispatch,
            state,
            commit,
        }, options) {
            return new Promise((resolve, reject) => {
                dispatch('log', {
                    level: 'info',
                    message: 'Got printout request',
                });

                // eslint-disable-next-line no-unused-vars
                const continuePrinting = async (kioskResult) => {
                    const { imageMap, result } = kioskResult;

                    if (result.recipe == null) {
                        return;
                    }

                    let l = result.recipe.location;
                    if (l.startsWith('//')) {
                        l = `https:${l}`;
                    }

                    const recipe = await (await fetch(l)).json();

                    const payload = {
                        items: [],
                    };

                    // These are not in the recipe!

                    // Main header.
                    payload.items.push({
                        type: 'header',
                        value: state.contactName || '',
                    });

                    payload.items.push({
                        type: 'space',
                        value: 2,
                    });

                    payload.items.push({
                        type: 'text',
                        value: `Phone: ${new AsYouType('US').input(state.contactPhone || '')}`,
                    });

                    // Is it two pack?
                    if (recipe.custom['fun-two-pack'] === 'true') {
                        payload.items.push({
                            type: 'space',
                            value: 1,
                        });

                        payload.items.push({
                            type: 'header',
                            value: 'Two Pack',
                        });

                        payload.items.push({
                            type: 'space',
                            value: 2,
                        });
                    } else {
                        payload.items.push({
                            type: 'space',
                            value: 2,
                        });
                    }

                    const addPayload = async (addBuild, addCheckout, showName, pop) => {
                        if (addBuild) {
                            const controllerId = pop.custom['fun-controller-id'];

                            if (imageMap[controllerId]) {
                                const image = await fetchAndResizeImageAsBase64(imageMap[controllerId], 512, 512);

                                payload.items.push({
                                    type: 'image',
                                    value: image,
                                });

                                payload.items.push({
                                    type: 'space',
                                    value: 2,
                                });
                            }
                        }

                        if (showName) {
                            let printName = `Name on the box: ${pop.custom['fun-name']}`;

                            if (addCheckout) {
                                printName = `Pop! ${pop.custom['fun-name']}`;
                            }

                            if (pop.custom['fun-name']) {
                                payload.items.push({
                                    type: 'text',
                                    value: printName,
                                    bold: true,
                                });

                                if (addBuild) {
                                    if (state.globalSettings?.blueprint?.custom?.['fun-print-name-code'] === 'code128') {
                                        payload.items.push({
                                            type: 'space',
                                            value: 1,
                                        });

                                        payload.items.push({
                                            type: 'small-header',
                                            value: 'BOX NAME',
                                        });

                                        payload.items.push({
                                            type: 'code128',
                                            value: pop.custom['fun-name'],
                                        });
                                    }

                                    if (state.globalSettings?.blueprint?.custom?.['fun-print-name-code'] === 'qrcode') {
                                        payload.items.push({
                                            type: 'space',
                                            value: 1,
                                        });

                                        payload.items.push({
                                            type: 'small-header',
                                            value: 'BOX NAME',
                                        });

                                        payload.items.push({
                                            type: 'qrcode',
                                            value: pop.custom['fun-name'],
                                        });
                                    }
                                }
                            } else {
                                payload.items.push({
                                    type: 'text',
                                    value: 'No name on the box',
                                });
                            }

                            if (addCheckout) {
                                payload.items.push({
                                    type: 'space',
                                    value: 2,
                                });
                            }
                        }

                        if (addBuild) {
                            payload.items.push({
                                type: 'space',
                                value: 2,
                            });

                            payload.items.push({
                                type: 'header',
                                value: 'BUILD',
                            });

                            payload.items.push({
                                type: 'space',
                                value: 2,
                            });
                        }

                        let index = -1;

                        const items = [];

                        // eslint-disable-next-line no-restricted-syntax
                        for (const s of pop.components) {
                            index += 1;

                            if (!s.component.placeholder) {
                                const placement = s.custom && s.custom['fun-ticket-name'];

                                const description = s.component.description && s.component.description.length > 0 && s.component.description[0];

                                const descriptionName = description.name;
                                const descriptionDescription = description.description;

                                const sku = s.component.custom['fun-sku'];
                                const upc = s.component.custom['fun-upc'];

                                const inCheckout = s.custom && s.custom['fun-checkout'] === 'true';
                                const inBuild = s.custom == null || s.custom['fun-build'] !== 'false';

                                const order = s.custom
                                    && s.custom['fun-order'] ? +s.custom['fun-order'] : (10000 + (index * 100));

                                if (placement && description && sku && upc) {
                                    let quantity = 1;

                                    if (s.component.quantityMultiplier) {
                                        quantity = +s.component.quantityMultiplier;
                                    }

                                    for (let i = 0; i < quantity; i += 1) {
                                        let placementName = placement;

                                        if (quantity !== 1) {
                                            placementName += ` (${i + 1} of ${quantity})`;
                                        }

                                        if (inCheckout && addCheckout) {
                                            items.push({
                                                type: 'sku',
                                                order,
                                                placement: placementName,
                                                shortName: descriptionName,
                                                longName: descriptionDescription,
                                                sku,
                                                upc,
                                                fromPlacement: s.code,
                                                fromComponent: s.component.code,
                                            });
                                        }

                                        if (inBuild && addBuild) {
                                            items.push({
                                                type: 'sku',
                                                order,
                                                placement: placementName,
                                                shortName: descriptionName,
                                                longName: descriptionDescription,
                                                sku,
                                                upc,
                                                fromPlacement: s.code,
                                                fromComponent: s.component.code,
                                            });
                                        }
                                    }
                                }
                            }
                        }

                        items.sort((a, b) => a.order - b.order);

                        // eslint-disable-next-line no-restricted-syntax
                        for (const i of items) {
                            payload.items.push(i);
                        }
                    };

                    if (recipe.assembly) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const a of recipe.assembly) {
                            if (a.recipe) {
                                // eslint-disable-next-line no-await-in-loop
                                await addPayload(true, false, true, a.recipe);
                            }
                        }
                    } else {
                        await addPayload(true, false, true, recipe);
                    }

                    payload.items.push({
                        type: 'space',
                        value: 2,
                    });

                    payload.items.push({
                        type: 'header',
                        value: 'CHECKOUT',
                    });

                    payload.items.push({
                        type: 'space',
                        value: 2,
                    });

                    addPayload(false, true, false, recipe);

                    if (recipe.assembly) {
                        payload.items.push({
                            type: 'space',
                            value: 2,
                        });

                        // eslint-disable-next-line no-restricted-syntax
                        for (const a of recipe.assembly) {
                            if (a.recipe) {
                                // eslint-disable-next-line no-await-in-loop
                                await addPayload(false, true, true, a.recipe);
                            }
                        }
                    }

                    dispatch('log', {
                        level: 'info',
                        message: `Print payload: ${JSON.stringify(payload, null, '    ')}`,
                    });

                    dispatch('log', {
                        level: 'info',
                        message: 'Sending to host',
                    });

                    if (state.options.onPrint) {
                        state.options.onPrint(payload).then((status) => {
                            resolve(status);
                        }).catch((error) => {
                            reject(error);
                        });
                    } else {
                        // Emulated mode.
                        resolve();
                    }
                };

                if (options == null || !options.reprint || state.lastPrintRecipe == null) {
                    dispatch('log', {
                        level: 'info',
                        message: 'Saving a recipe',
                    });

                    dispatch('customizerDone', {
                        noBusy: true,
                        saveOnly: true,
                        saveOnlyKiosk: true,
                    }).then((result) => {
                        // If we arrived here, the inventory check was ok.
                        dispatch('log', {
                            level: 'info',
                            message: `Saved recipe: ${JSON.stringify(result, null, '    ')}`,
                        });

                        commit('SET_LAST_PRINT_RECIPE', result);

                        continuePrinting(result);
                    }).catch((error) => {
                        reject(error);
                    });
                } else {
                    dispatch('log', {
                        level: 'info',
                        message: 'Not saving a recipe',
                    });

                    continuePrinting(state.lastPrintRecipe);
                }
            });
        },

        /**
         * Propagate names from each pop to the main controller and render names on the two-pack box.
         */
        syncTwoPackNames({ state, getters }) {
            if (getters.isTwoPack) {
                state.mainController.allSockets().forEach((s) => {
                    if (s.to) {
                        let toPlacement = null;

                        if (s.socket.code === 'POPLeft') {
                            toPlacement = 'POPBoxTextureText';
                        }

                        if (s.socket.code === 'POPRight') {
                            toPlacement = 'POPBoxTextureText2';
                        }

                        if (toPlacement) {
                            const nameComponent = s.to.selectedAtPlacement('POPBoxTextureText');

                            const name = nameComponent?.custom?.['personalization-text'];

                            if (name) {
                                state.mainController.updatePersonalization(toPlacement, name);
                            }
                        }
                    }
                });
            }
        },

        /**
         * Used to prevent layout breakage when the keyboard is open on mobile.
         */
        setIsKeyboardOpen({ commit }, v) {
            commit('SET_IS_KEYBOARD_OPEN', v);
        },

        setComponentQuantity(_, {
            component,
            quantity,
        }) {
            Vue.set(component, 'quantityMultiplier', quantity);
        },

        setMainIcon({ commit }, v) {
            commit('SET_MAIN_ICON', v);
        },

        saveCart({ commit }, v) {
            commit('SET_CART', v);
        },

        removeAlertFromPlacement({ state, commit }, placement) {
            // Remove alert first.
            let selectGroup;

            state.viewController.blueprint.groups.forEach((group) => {
                if (group.placements.some((p) => p.code === placement)) {
                    selectGroup = group;
                }
            });

            if (selectGroup) {
                commit('REMOVE_ALERT_GROUP', selectGroup.code);
            }
        },

        syncLogos({ getters }) {
            // eslint-disable-next-line no-restricted-syntax
            for (const pop of getters.popControllers) {
                // Check for logos. Currently tops only.
                if (pop) {
                    const top = pop.selectedAtPlacement(constants.PLACEMENT_TORSO);
                    const topLogo = pop.findPlacement(constants.PLACEMENT_TORSO_LOGO);

                    if (top && topLogo) {
                        let found = false;

                        if (top?.custom['fun-apply-logo']) {
                            const logoCode = top.custom['fun-apply-logo'];
                            const logo = topLogo.components.find((c) => c.placementIndependentCode === logoCode && pop.isAvailable(c));
                            if (logo) {
                                // eslint-disable-next-line no-unused-vars
                                const result = pop.updateComponent(constants.PLACEMENT_TORSO_LOGO, logo.code);
                                found = true;
                            }
                        }

                        if (!found) {
                            const placeholder = topLogo.components.find((c) => c.placeholder && pop.isAvailable(c));
                            if (placeholder) {
                                pop.updateComponent(constants.PLACEMENT_TORSO_LOGO, placeholder.code);
                            }
                        }
                    }
                }
            }
        },

        updateComponent({ state, dispatch, commit }, { placement, code }) {
            dispatch('uiInteracted');

            return new Promise((resolve) => {
                dispatch('log', {
                    level: 'info',
                    message: `Select ${code} on ${placement}`,
                    style: 'color: #009; font-weight: bold;',
                });

                // Make a clone first.
                const copy = state.viewController.clone();

                // Count a number of placeholders.
                const placeholdersBefore = copy.state.filter((x) => x.placement.code !== placement && x.component.placeholder).length;

                const nonEmptyBefore = copy.state.filter((x) => !x.component.placeholder).map((x) => x.placement.code);

                // Apply the change.
                copy.updateComponent(placement, code);

                commit('BUMP_VERSION');

                // Placeholders after.
                const placeholdersAfter = copy.state.filter((x) => x.component.placeholder && x.placement.code !== placement).length;

                const emptyAfter = copy.state
                    .filter((x) => x.component.placeholder)
                    .map((x) => x.placement.code);

                // Abandon the clone anyway.
                copy.off();

                // A hack for bottoms that "appear" after switching from outfit to non-outfit.
                // Have we "seen" steps that come after bottoms?
                const hadBeenPastBottoms = state.seenGroups.includes('Accessories1')
                    || state.seenGroups.includes('Accessories2')
                    || state.seenGroups.includes('Accessories3');
                // Does this work for babies?
                let hadDisabledBottoms = false;
                const placementLegs = state.viewController.findPlacement('POPLegs');
                if (placementLegs && placementLegs.components) {
                    hadDisabledBottoms = !placementLegs.components.some((c) => !c.placeholder && state.viewController.isAvailable(c));
                }

                const checkBottoms = () => {
                    // Have we seen legs before?
                    if (hadBeenPastBottoms) {
                        if (hadDisabledBottoms) {
                            if (placementLegs && placementLegs.components) {
                                const haveBottoms = placementLegs.components.some((c) => !c.placeholder && state.viewController.isAvailable(c));

                                // If we didn't have bottoms before, but we do now.
                                // Alert the step.
                                if (haveBottoms) {
                                    commit('ADD_SEEN_GROUP', 'Legs');
                                    commit('ADD_ALERT_GROUP', 'Legs');
                                }
                            }
                        }
                    }
                };

                // If placeholder counts don't match, warn the user, otherwise apply the change.
                if (placeholdersBefore < placeholdersAfter) {
                    dispatch('log', {
                        level: 'info',
                        message: '... this change would reset some placements, showing a warning',
                    });

                    dispatch('showChangeWarning', {
                        confirm: () => {
                            dispatch('log', {
                                level: 'info',
                                message: '... change accepted',
                            });

                            dispatch('removeAlertFromPlacement', placement);

                            state.viewController.updateComponent(placement, code);

                            commit('SET_DIRTY_BY_CONTROLLER', state.viewController);

                            const emptyGroups = [];

                            // Review all groups that were reset.
                            emptyAfter.forEach((emptyNow) => {
                                if (nonEmptyBefore.includes(emptyNow)) {
                                    // Find the respective group.
                                    let resetGroup;

                                    state.viewController.blueprint.groups.forEach((group) => {
                                        if (group.placements.some((p) => p.code === emptyNow)) {
                                            resetGroup = group;
                                        }
                                    });

                                    if (resetGroup && !emptyGroups.includes(resetGroup)) {
                                        emptyGroups.push(resetGroup);
                                    }
                                }
                            });

                            // Add previously alerted groups.
                            state.alertGroups.forEach((v) => {
                                const alertGroup = state.viewController.blueprint.groups.find((x) => x.code === v);

                                if (!emptyGroups.includes(alertGroup)) {
                                    emptyGroups.push(alertGroup);
                                }
                            });

                            // Review empty groups.
                            emptyGroups.forEach((emptyGroup) => {
                                // Does it have any available components left.
                                let available = false;

                                for (let placementIndex = 0; placementIndex < emptyGroup.placements.length; placementIndex += 1) {
                                    const p = emptyGroup.placements[placementIndex];

                                    for (let cIndex = 0; cIndex < p.components.length; cIndex += 1) {
                                        const c = p.components[cIndex];

                                        if (!c.placeholder && state.viewController.isAvailable(c)) {
                                            available = c;
                                            break;
                                        }
                                    }

                                    if (available) {
                                        break;
                                    }
                                }

                                // If not available, remove from seen groups.
                                if (!available) {
                                    commit('SET_SEEN_GROUPS', state.seenGroups.filter((x) => x !== emptyGroup.code));
                                } else {
                                    // Otherwise mark as alerted.
                                    const placementCodes = emptyGroup.placements.map((p) => p.code);
                                    const placementCodeIsInPlacements = placementCodes.includes(placement);
                                    if (placementCodeIsInPlacements) {
                                        return;
                                    }
                                    commit('ADD_ALERT_GROUP', emptyGroup.code);
                                }
                            });

                            checkBottoms();

                            dispatch('syncLogos');

                            dispatch('validateSpecialBoxes');

                            resolve();
                        },
                        cancel: () => {
                            dispatch('log', {
                                level: 'info',
                                message: '... rejected',
                            });
                        },
                    });
                } else {
                    dispatch('removeAlertFromPlacement', placement);

                    state.viewController.updateComponent(placement, code);

                    commit('SET_DIRTY_BY_CONTROLLER', state.viewController);

                    dispatch('validateSeenGroups');

                    checkBottoms();

                    dispatch('syncLogos');

                    dispatch('validateSpecialBoxes');

                    resolve();
                }
            });
        },

        validateSeenGroups({ state, commit }) {
            // Review alerted groups.
            [...state.alertGroups, ...state.seenGroups].forEach((v) => {
                const alertGroup = state.viewController.blueprint.groups.find((x) => x.code === v);

                if (alertGroup == null) {
                    return;
                }

                const isNoComponentsStep = !alertGroup.placements.length;

                // Does it have any available components left.
                let available = false;

                for (let placementIndex = 0; placementIndex < alertGroup.placements.length; placementIndex += 1) {
                    const p = alertGroup.placements[placementIndex];

                    for (let cIndex = 0; cIndex < p.components.length; cIndex += 1) {
                        const c = p.components[cIndex];

                        if (!c.placeholder && state.viewController.isAvailable(c)) {
                            available = c;
                            break;
                        }
                    }

                    if (available) {
                        break;
                    }
                }

                // Prevent Name and Review navigation buttons from being disabled
                if (isNoComponentsStep) {
                    available = true;
                }

                // If not available, remove from seen groups.
                if (!available) {
                    commit('SET_SEEN_GROUPS', state.seenGroups.filter((x) => x !== alertGroup.code));
                    commit('REMOVE_ALERT_GROUP', alertGroup.code);
                }
            });
        },

        setSavedTickets({ commit }, v) {
            commit('SET_SAVED_TICKETS', v);
        },

        reloadInventory({
            state,
            dispatch,
            commit,
            getters,
        }) {
            // Reset inventory cache.
            outOfStockResetCache();

            // Use the first pop controller.
            const firstController = getters.popControllers[0];

            if (firstController) {
                dispatch('log', {
                    level: 'error',
                    message: 'Reloading inventory',
                });

                const controller = new state.options.DriveCustomizer.Controller(
                    { ...state.defaultControllerOptions, ...{ products: firstController.products() } },
                );

                // controller.traceAllowed = true;
                // controller.traceAvailable = true;
                // controller.traceUpdated = true;

                controller.on('afterAssembliesBuilt.store', () => {
                    dispatch('log', {
                        level: 'info',
                        message: 'Got a replacement controller',
                    });

                    controller.blueprint.options.forEach((o) => {
                        getters.popControllers.forEach((p) => {
                            const old = p.blueprint.options.find((c) => c.code === o.code);

                            if (old) {
                                old.inventory = o.inventory;
                            }
                        });
                    });

                    dispatch('log', {
                        level: 'info',
                        message: 'Copied availability',
                    });

                    let wentToAlert = false;

                    // eslint-disable-next-line no-restricted-syntax
                    for (const pop of getters.popControllers) {
                        dispatch('selectPop', pop);

                        const toReset = [];

                        pop.state.forEach((s) => {
                            if (s.component.placeholder) {
                                return;
                            }

                            const q = s.component.quantityMultiplier || 1;

                            if (!pop.isAvailable(s.component, q)) {
                                toReset.push(s.placement.code);
                            }
                        });

                        dispatch('log', {
                            level: 'info',
                            message: `Will need to reset ${toReset.join(', ')}`,
                        });

                        if (toReset.length > 0) {
                            toReset.forEach((p) => {
                                const placement = pop.findPlacement(p);

                                if (placement && placement.components) {
                                    const placeholder = placement.components.find((c) => c.placeholder);

                                    if (placeholder) {
                                        dispatch('log', {
                                            level: 'info',
                                            message: `Reset to placeholder ${placeholder.code} on ${p}`,
                                        });

                                        pop.updateComponent(p, placeholder.code);

                                        // Mark group as alerted.
                                        let selectGroup;

                                        pop.blueprint.groups.forEach((group) => {
                                            if (group.placements.some((otherP) => otherP.code === p)) {
                                                selectGroup = group;
                                            }
                                        });

                                        if (selectGroup) {
                                            commit('ADD_ALERT_GROUP', selectGroup.code);
                                        }
                                    }
                                }
                            });

                            dispatch('log', {
                                level: 'info',
                                message: 'Validating groups',
                            });

                            dispatch('validateSeenGroups');

                            if (state.alertGroups.length > 0) {
                                // Go to the first alerted group.
                                dispatch('log', {
                                    level: 'info',
                                    message: `Going back to alert group ${state.alertGroups[0]}`,
                                });

                                const goBack = pop.blueprint.groups
                                    .find((g) => state.alertGroups.some((a) => a === g.code) && g.custom['fun-show-in-nav'] === 'true');

                                if (goBack) {
                                    dispatch('showGroup', goBack);

                                    wentToAlert = true;
                                }
                            }
                        }
                    }

                    if (!wentToAlert) {
                        // Fallback.
                        // Go to the last selected group.
                        dispatch('log', {
                            level: 'info',
                            message: 'Going to the previous special group Review',
                        });

                        dispatch('showGroup', {
                            code: 'Name',
                        });
                    }
                });

                controller.load();
            }
        },

        bumpVersion({ commit }) {
            commit('BUMP_VERSION');
        },

        setRecipeOverride({ commit }, v) {
            commit('SET_RECIPE_OVERRIDE', v);
        },

        /**
         * Synchronize pets between the controller state and respective preview and "cached" pets.
         */
        syncPets({ commit, getters, state }) {
            getters.popControllers.forEach((controller) => {
                const pets = [];

                // Make a list of all pets.
                constants.PLACEMENT_PETS.forEach((placement) => {
                    const component = controller.selectedAtPlacement(placement);

                    if (component && !component.placeholder) {
                        // Show duplicates of pets...
                        const q = component.quantityMultiplier || 1;

                        for (let i = 0; i < q; i += 1) {
                            pets.push(component);
                        }
                    }
                });

                if (pets.length > 2) {
                    const firstPet = pets[0].code;
                    const isDifferentPet = (pet) => pet.code !== firstPet;
                    const differentPetIndex = pets.findIndex(isDifferentPet);

                    if (differentPetIndex) {
                        const element = pets.splice(differentPetIndex, 1)[0];

                        pets.splice(1, 0, element);
                    }
                }

                let placement = controller.findPlacement(constants.PLACEMENT_PET_PREVIEW);
                if (placement) {
                    if (pets.length > 0) {
                        const select = placement.components.find((c) => c.placementIndependentCode === pets[0].placementIndependentCode);

                        if (select) {
                            controller.updateComponent(constants.PLACEMENT_PET_PREVIEW, select.code, {
                                ignoreAvailability: false,
                                checkAvailability: false,
                            });
                        }
                    } else {
                        const select = placement.components.find((c) => c.placeholder);

                        if (select) {
                            controller.updateComponent(constants.PLACEMENT_PET_PREVIEW, select.code);
                        }
                    }
                }

                placement = controller.findPlacement(constants.PLACEMENT_PET_PREVIEW_SECONDARY);
                if (placement) {
                    if (pets.length > 1) {
                        const select = placement.components.find((c) => c.placementIndependentCode === pets[1].placementIndependentCode);

                        if (select) {
                            controller.updateComponent(constants.PLACEMENT_PET_PREVIEW_SECONDARY, select.code, {
                                ignoreAvailability: false,
                                checkAvailability: false,
                            });
                        }
                    } else {
                        const select = placement.components.find((c) => c.placeholder);

                        if (select) {
                            controller.updateComponent(constants.PLACEMENT_PET_PREVIEW_SECONDARY, select.code);
                        }
                    }
                }

                commit('SET_PETS_BY_CONTROLLER', {
                    controller,
                    pets: petsOf(controller, state),
                });
            });
        },

        /**
         * Synchronize attach packs between the controller state and respective preview and "cached" aps.
         */
        syncAttachPacks({
            commit,
            dispatch,
            getters,
            state,
            rootState,
        }) {
            getters.popControllers.forEach((controller) => {
                const packs = [];

                // Make a list of all packs.
                constants.PLACEMENT_PACKS.forEach((placement) => {
                    const component = controller.selectedAtPlacement(placement);

                    if (component && !component.placeholder) {
                        // Show duplicates of packs...
                        const q = component.quantityMultiplier || 1;

                        for (let i = 0; i < q; i += 1) {
                            packs.push(component);
                        }
                    }
                });

                if (packs.length > 2) {
                    const firstPack = packs[0].code;
                    const isDifferentPack = (ap) => ap.code !== firstPack;
                    const differentPackIndex = packs.findIndex(isDifferentPack);

                    if (differentPackIndex) {
                        const element = packs.splice(differentPackIndex, 1)[0];

                        packs.splice(1, 0, element);
                    }
                }

                let placement = controller.findPlacement(constants.PLACEMENT_AP_PREVIEW);
                if (placement) {
                    if (packs.length > 0) {
                        const select = placement.components.find((c) => c.placementIndependentCode === packs[0].placementIndependentCode);

                        if (select) {
                            controller.updateComponent(constants.PLACEMENT_AP_PREVIEW, select.code, {
                                ignoreAvailability: false,
                                checkAvailability: false,
                            });
                        }
                    } else {
                        const select = placement.components.find((c) => c.placeholder);

                        if (select) {
                            controller.updateComponent(constants.PLACEMENT_AP_PREVIEW, select.code);
                        }
                    }
                }

                placement = controller.findPlacement(constants.PLACEMENT_AP_PREVIEW_SECONDARY);
                if (placement) {
                    if (packs.length > 1) {
                        const select = placement.components.find((c) => c.placementIndependentCode === packs[1].placementIndependentCode);

                        if (select) {
                            controller.updateComponent(constants.PLACEMENT_AP_PREVIEW_SECONDARY, select.code, {
                                ignoreAvailability: false,
                                checkAvailability: false,
                            });
                        }
                    } else {
                        const select = placement.components.find((c) => c.placeholder);

                        if (select) {
                            controller.updateComponent(constants.PLACEMENT_AP_PREVIEW_SECONDARY, select.code);
                        }
                    }
                }

                commit('SET_ATTACH_PACKS_BY_CONTROLLER', {
                    controller,
                    attachPacks: attachPacksOf(controller),
                });
            });

            if (state.viewController) {
                const currentPacks = [];

                // Make a list of all packs.
                constants.PLACEMENT_PACKS.forEach((placement) => {
                    const component = state.viewController.selectedAtPlacement(placement);

                    if (component && !component.placeholder) {
                        currentPacks.push(component);
                    }
                });

                if (currentPacks.length > 0) {
                    if (rootState.isSpellingBee) {
                        const first = currentPacks[0];

                        if (first.custom['fun-sb-type']) {
                            dispatch('setSpellingBeeType', first.custom['fun-sb-type']);
                        }
                    } else {
                        dispatch('setSpellingBeeType', null);
                    }
                } else {
                    dispatch('setSpellingBeeType', null);
                }
            }
        },

        setRotation({ commit }, v) {
            commit('SET_ROTATION', v);
        },

        setPreviewRotation({ commit }, v) {
            commit('SET_PREVIEW_ROTATION', v);
        },

        setIsMobile({ commit }, v) {
            commit('SET_IS_MOBILE', v);
        },

        setIsMobileApp({ commit }, v) {
            commit('SET_IS_MOBILE_APP', v);
        },

        setIsSubMenuOpen({ commit }, v) {
            commit('SET_IS_SUB_MENU_OPEN', v);
        },

        setIsNavMenuOpen({ commit }, v) {
            commit('SET_IS_NAV_MENU_OPEN', v);
        },

        setDidNavigateFromMainMenu({ commit }, v) {
            commit('SET_DID_NAVIGATE_FROM_MAIN_MENU', v);
        },

        setSelectedSwatchColors({ commit }, v) {
            commit('SET_SELECTED_SWATCH_COLORS', v);
        },

        setPreviewImages({ commit }, v) {
            commit('SET_PREVIEW_IMAGES', v);
        },

        setAttachPacksPreviewImage({ commit }, v) {
            commit('SET_ATTACH_PACKS_PREVIEW_IMAGE', v);
        },

        setPreviewImageByController({ commit }, v) {
            commit('SET_PREVIEW_IMAGE_BY_CONTROLLER', v);
        },

        setCartImageByController({ commit }, v) {
            commit('SET_CART_IMAGE_BY_CONTROLLER', v);
        },

        setCartImageBySpecificController({ commit }, payload) {
            commit('SET_CART_IMAGE_BY_SPECIFIC_CONTROLLER', payload);

            commit('BUMP_VERSION');
        },

        /**
         * Selects a pop currently visible in the configurator.
         */
        selectPop({ dispatch, state, commit }, pop) {
            // Show the pop.
            dispatch('showController', {
                main: state.mainController,
                view: pop,
            });

            // Unstash seen and alerted groups, box name, selected swatch colors.
            commit('SET_SEEN_GROUPS', state.seenGroupsByController[pop.id] || []);
            commit('SET_ALERT_GROUPS', state.alertGroupsByController[pop.id] || []);
            commit('SET_BOX_NAME', state.boxNamesByController[pop.id] ?? '');

            dispatch('checkStashedAiSelections');

            state.selectedSwatchColors = state.selectedSwatchColorsByController[pop.id] || {
                'Hair & Hats': null,
                Beard: null,
            };

            updateSelectedSwatchColors(state);
        },

        /**
         * Add seasonal packaging.
         */
        addSeasonalBox({ state, getters, dispatch }) {
            // Pop "version" bumping is necessary when the pop was modified while on the checkout screen
            // so we can't simply reprint anymore.
            dispatch('bumpVersion');

            const box = getters.seasonalBox;

            if (box == null || box.selected) {
                return;
            }

            state.mainController.addTag(box.tag);
            state.mainController.updateComponent(box.placement, box.component.code);
        },

        /**
         * Remove seasonal box.
         */
        removeSeasonalBox({ state, getters, dispatch }) {
            dispatch('bumpVersion');

            const box = getters.seasonalBox;

            if (box == null || !box.selected) {
                return;
            }

            state.mainController.removeTag(box.tag);

            // Now that the controlling tag was removed, find the first available box in that placement.
            const replacement = state.mainController.findPlacement(box.placement).components.find((x) => !x.placeholder && state.mainController.isAvailable(x));

            if (replacement) {
                state.mainController.updateComponent(box.placement, replacement.code);
            }
        },

        storeDefaultBox({ state, commit }) {
            // Get current box details.
            if (state.defaultBox == null) {
                const currentBox = state.mainController.selectedAtPlacement(constants.PLACEMENT_BOX);

                if (currentBox) {
                    commit('SET_DEFAULT_BOX', {
                        code: currentBox.code,
                        image: currentBox.custom.image,
                        title: currentBox.custom['fun-box-title'] ?? currentBox.name,
                    });
                }
            }
        },

        /**
         * Revalidate boxes and remove invalid ones.
         */
        validateSpecialBoxes({ state, getters, rootState }) {
            getSpecialBoxes(state, getters, rootState, {
                onRemove: () => {
                    let boxPlacement = state.mainController.findPlacement(constants.PLACEMENT_BOX);
                    if (boxPlacement == null) {
                        boxPlacement = state.mainController.findPlacement(constants.PLACEMENT_BOX_PACK);
                    }

                    // Now that the controlling tag was removed, find the first available box in that placement.
                    const replacement = boxPlacement.components.find((x) => !x.placeholder && state.mainController.isAvailable(x));

                    if (replacement) {
                        const selected = state.mainController.selectedAtPlacement(boxPlacement.code);

                        if (selected === null || selected.code !== replacement.code) {
                            state.mainController.updateComponent(boxPlacement.code, replacement.code);
                        }
                    }
                },
            });
        },

        /**
         * Add special packaging.
         */
        addSpecialBox({ state, dispatch, getters }, box) {
            // Pop "version" bumping is necessary when the pop was modified while on the checkout screen
            // so we can't simply reprint anymore.
            dispatch('bumpVersion');

            // Unselect other special box.
            const otherBox = getters.specialBoxes.find((x) => x.selected);
            if (otherBox != null) {
                dispatch('removeSpecialBox');
            }

            dispatch('storeDefaultBox');

            if (box == null || box.selected) {
                return;
            }

            state.mainController.addTag(box.tag);
            state.mainController.updateComponent(box.placement, box.component.code);
        },

        /**
         * Remove seasonal box.
         */
        removeSpecialBox({ state, getters, dispatch }) {
            dispatch('bumpVersion');

            const box = getters.specialBoxes.find((x) => x.selected);

            if (box == null) {
                return;
            }

            state.mainController.removeTag(box.tag);

            // Now that the controlling tag was removed, find the first available box in that placement.
            const replacement = state.mainController.findPlacement(box.placement).components.find((x) => !x.placeholder && state.mainController.isAvailable(x));

            if (replacement) {
                state.mainController.updateComponent(box.placement, replacement.code);
            }
        },

        /**
         * Attempt to print a ticket.
         */
        printFlow({
            state,
            getters,
            dispatch,
            commit,
        }) {
            dispatch('showPrintingProgress', true);

            // Re-get inventory.
            dispatch('getPrices', {
            }).then(() => {
                // Validate, are all selected components still available.
                let outOfStock = false;

                // eslint-disable-next-line no-restricted-syntax
                for (const pop of getters.popControllers) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const s of pop.state) {
                        if (s.component && s.component.custom['fun-sku']) {
                            const q = s.component.quantityMultiplier || 1;

                            if (!pop.isAvailable(s.component, q)) {
                                outOfStock = true;

                                dispatch('log', {
                                    level: 'info',
                                    message: `${s.component.code} is out of stock`,
                                });
                            }
                        }
                    }
                }

                if (outOfStock) {
                    dispatch('showPrintingProgress', false);

                    commit('SET_PRINT_ATTEMPTED', false);

                    dispatch('reloadInventory');

                    dispatch('setLastError', 'inventory');

                    dispatch('showPrintingError', 'inventory');

                    return;
                }

                let oldState = state.printAttempted;

                if (state.printAttemptedAtVersion != null && state.printAttemptedAtVersion !== state.configurationVersion) {
                    oldState = false;
                }

                commit('SET_PRINT_ATTEMPTED', true);
                commit('SET_PRINT_ATTEMPTED_AT_VERSION', state.configurationVersion);

                if (state.recipeOverride) {
                    dispatch('setRecipeOverride', false);

                    oldState = true;
                }

                dispatch('print', {
                    reprint: oldState,
                }).then(() => {
                    dispatch('showPrintingProgress', false);

                    dispatch('showGroup', {
                        code: 'Confirm',
                    });
                }).catch((error) => {
                    let validationMessage = error;

                    if (validationMessage && validationMessage.response && validationMessage.response.data && validationMessage.response.data.modelState) {
                        const errors = [];

                        Object.keys(validationMessage.response.data.modelState).forEach((k) => {
                            errors.push(validationMessage.response.data.modelState[k]);
                        });

                        validationMessage = errors.join(',');
                    }

                    if (validationMessage && validationMessage.includes && validationMessage.includes('inventory')) {
                        // This is an inventory error.
                        // Reload the blueprint.

                        dispatch('reloadInventory');

                        commit('SET_PRINT_ATTEMPTED', false);
                    }

                    if (error) {
                        const message = error.toString().toLowerCase();

                        if (message.includes('timeout')) {
                            commit('SET_PRINT_ATTEMPTED', false);
                        }

                        if (message.includes('network')) {
                            commit('SET_PRINT_ATTEMPTED', false);
                        }

                        if (message.includes('status code')) {
                            commit('SET_PRINT_ATTEMPTED', false);
                        }
                    }

                    dispatch('showPrintingProgress', false);

                    dispatch('showPrintingError', error);
                });
            });
        },

        setNoSavePreviews({ commit }, v) {
            commit('SET_NO_SAVE_PREVIEWS', v);
        },

        setSavedRecipe({ commit }, v) {
            commit('SET_SAVED_RECIPE', v);
        },

        setSelectedRecommendations({ commit }, v) {
            commit('SET_SELECTED_RECOMMENDATIONS', v);
        },

        setAiSelections({ commit }, v) {
            commit('SET_AI_SELECTIONS', v);
        },

        setAiMatchError({ commit }, v) {
            commit('SET_AI_MATCH_ERROR', v);
        },

        setAiPreview({ commit, state }, v) {
            return new Promise((resolve) => {
                // Is this heic by any chance?
                const colon = v.sourceImage.indexOf(':');
                const comma = v.sourceImage.indexOf(';', colon);
                const mimeType = v.sourceImage.substr(colon + 1, comma - colon - 1);

                if (mimeType.toLowerCase() === 'image/heic') {
                    // Have to convert it first.
                    let url = 'https://prd-customizer-int-funko-ai-app-fyfecshwfvbsh7cd.eastus2-01.azurewebsites.net/api/v1/selfie/match';

                    if (state.globalSettings) {
                        if (state.globalSettings.blueprint.custom['fun-ai-convert-service']) {
                            url = state.globalSettings.blueprint.custom['fun-ai-convert-service'];
                        }
                    }

                    const payload = {
                        image: v.sourceImage,
                    };

                    fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                    })
                        .then((response) => {
                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`);
                            }

                            return response.json();
                        })
                        .then((data) => {
                            commit('SET_AI_PREVIEW', {
                                sourceImage: data.image,
                                orientation: v.orientation,
                            });

                            resolve();
                        });
                } else {
                    commit('SET_AI_PREVIEW', v);

                    resolve();
                }
            });
        },

        async buildFromSelfie({ dispatch, state, rootState }) {
            let url = 'https://prd-customizer-int-funko-ai-app-fyfecshwfvbsh7cd.eastus2-01.azurewebsites.net/api/v1/selfie/match';
            let type = 'standard';

            if (state.globalSettings) {
                if (state.globalSettings.blueprint.custom['fun-ai-service']) {
                    url = state.globalSettings.blueprint.custom['fun-ai-service'];
                }

                if (state.globalSettings.blueprint.custom['fun-ai-service-type'] === 'alt' || window.location.href.includes('selfieservice=alt')) {
                    type = 'alt';
                    url = state.globalSettings.blueprint.custom['fun-ai-service-alt'];
                }
            }
            // const url = 'http://localhost:3100/api/v1/selfie/match';

            const payload = {
                image: state.aiPreview.sourceImage,
            };

            if (state.viewController) {
                payload.blueprintFrom = state.viewController.resolveBlueprintUrl();
            } else {
                payload.blueprintFrom = state.globalSettings.resolveBlueprintUrl().replace('POPPeopleSettings', 'POPPeopleWebBase');
            }

            if (state.options.productDataEndpoint) {
                let useInventory = state.options.productDataEndpoint;

                if (useInventory.startsWith('/')) {
                    useInventory = `${window.location.protocol}//${window.location.host}${useInventory}`;
                }

                payload.inventoryFrom = useInventory;
            }

            // eslint-disable-next-line prefer-destructuring
            const {
                aiActiveState,
                aiStates,
                // eslint-disable-next-line no-unused-vars
                aiSelfieStates,
                aiUploadStates,
            } = rootState;

            const isSelfie = aiActiveState === aiStates.selfie;

            dispatch('setAiMatchError', null);

            const form = new FormData();

            const fileResponse = await fetch(state.aiPreview.sourceImage);
            const blob = await fileResponse.blob();
            const file = new File([blob], 'image.png', { type: blob.type });
            form.append('image', file);

            let request = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            };

            if (type === 'alt') {
                request = {
                    method: 'POST',
                    headers: {
                    },
                    body: form,
                };
            }

            fetch(url, request)
                .then(async (response) => {
                    dispatch('globalBusy', false);

                    if (!response.ok) {
                        // Try to parse JSON error body.
                        let errorBody;
                        try {
                            errorBody = await response.json();
                        } catch {
                            errorBody = null;
                        }

                        if (errorBody && errorBody.error) {
                            dispatch('setAiMatchError', errorBody.error);
                        }

                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    return response.json();
                })
                .then((data) => {
                    dispatch('setAiSelections', data);

                    console.log('Success:', data);

                    // Don't close AI modal until pops are loaded.

                    // Figure out the next steps.

                    // Do we have a view controller? Apply selections.
                    // Otherwise start loading, and we will defer the selections.

                    if (state.viewController) {
                        dispatch('checkStashedAiSelections');
                    } else {
                        // Begin customization.
                        dispatch('log', {
                            level: 'info',
                            message: 'Start customization from AI selector',
                        });

                        dispatch('startCustomization', {
                            mode: rootState.packType,
                        });

                        const eventName = rootState.packType === 'two-pack' ? 'pop_people_start_two_pack' : 'pop_people_start_single';

                        if (window.dataLayer) {
                            window.dataLayer.push({
                                event: eventName,
                            });
                        }
                    }

                    dispatch('setBuiltFromSelfie', true);
                })
                .catch(() => {
                    dispatch('globalBusy', false);
                    if (isSelfie) {
                        dispatch('setAiSelfieActiveState', aiSelfieStates.error);
                    } else {
                        dispatch('setAiUploadActiveState', aiUploadStates.error);
                    }
                });
        },

        checkStashedAiSelections({ dispatch, state }) {
            if (state.aiSelections) {
                dispatch('applyAiSelections', {
                    aiSelections: state.aiSelections,
                    controller: state.viewController,
                });

                // Advance to the last viable group, _up to_ Torsos.
                let groupToSelect = null;

                // eslint-disable-next-line no-restricted-syntax
                for (const group of state.viewController.blueprint.groups) {
                    if (!state.seenGroups.includes(group.code)) {
                        groupToSelect = group;
                        break;
                    }

                    if (group.code === 'Torso') {
                        groupToSelect = group;
                        break;
                    }
                }

                if (groupToSelect) {
                    dispatch('showGroup', groupToSelect);
                }

                // Clear once applied.
                dispatch('setAiSelections', null);
            }
        },

        seenUpToGroup({ dispatch }, {
            controller,
            placement,
        }) {
            let group = null;

            // eslint-disable-next-line no-restricted-syntax
            for (const g of controller.blueprint.groups) {
                if (g.placements.some((x) => x.code === placement)) {
                    group = g;
                    break;
                }
            }

            if (group) {
                // eslint-disable-next-line no-restricted-syntax
                for (const g of controller.blueprint.groups) {
                    dispatch('addSeenGroup', g.code);

                    if (g.code === group.code) {
                        break;
                    }
                }
            }
        },

        applyAiSelections({ dispatch }, {
            aiSelections,
            controller,
        }) {
            if (aiSelections) {
                // eslint-disable-next-line arrow-body-style
                const getComponentFromAlt = (type) => {
                    return aiSelections.results?.[0]
                        ?.targetFormat
                        ?.assembly
                        ?.find((x) => x.socket === 'POPLeft')
                        ?.recipe
                        ?.components
                        ?.find((x) => x.code === type);
                };

                // eslint-disable-next-line prefer-destructuring
                let gender = aiSelections.gender;
                if (gender == null) {
                    const altGender = getComponentFromAlt('POPBody');

                    if (altGender?.component?.code === 'POPBODY-BodyFemale') {
                        gender = 'FM';
                    }

                    if (altGender?.component?.code === 'POPBODY-BodyMale') {
                        gender = 'ML';
                    }
                }

                if (gender) {
                    const bodyPlacement = controller.findPlacement('POPBody');
                    if (gender === 'FM') {
                        const body = bodyPlacement.components.find((x) => x.code.includes('Female'));
                        if (body) {
                            controller.updateComponent(bodyPlacement.code, body.code);
                        }
                    } else {
                        const body = bodyPlacement.components.find((x) => x.code.includes('Male'));
                        if (body) {
                            controller.updateComponent(bodyPlacement.code, body.code);
                        }
                    }
                }

                // eslint-disable-next-line prefer-destructuring
                let tone = aiSelections.tone;
                if (tone == null) {
                    const number = aiSelections
                        .results?.[0]
                        ?.targetFormat?.components?.find((x) => x.code === 'POPColor')?.component?.custom['fun-skin-number'];
                    if (number != null) {
                        tone = `SK${number}`;
                    }
                }

                if (tone) {
                    const colorPlacement = controller.findPlacement('POPColor');
                    if (tone === 'SK1') {
                        const color = colorPlacement.components.find((x) => x.code.includes('1'));
                        if (color) {
                            controller.updateComponent(colorPlacement.code, color.code);
                        }
                    }

                    if (tone === 'SK3') {
                        const color = colorPlacement.components.find((x) => x.code.includes('3'));
                        if (color) {
                            controller.updateComponent(colorPlacement.code, color.code);
                        }
                    }

                    if (tone === 'SK4') {
                        const color = colorPlacement.components.find((x) => x.code.includes('4'));
                        if (color) {
                            controller.updateComponent(colorPlacement.code, color.code);
                        }
                    }

                    if (tone === 'SK5') {
                        const color = colorPlacement.components.find((x) => x.code.includes('5'));
                        if (color) {
                            controller.updateComponent(colorPlacement.code, color.code);
                        }
                    }
                }

                const makeSelections = (from, placement) => {
                    const skus = [];

                    if (from?.length > 0) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const s of from) {
                            if (s.sku) {
                                skus.push(s.sku);
                            }
                        }
                    }

                    // Alt fallback.
                    const alt = getComponentFromAlt(placement);
                    if (alt) {
                        const altSku = alt.component?.custom?.['fun-sku'];
                        if (altSku) {
                            skus.push(altSku);
                        }
                    }

                    // eslint-disable-next-line no-restricted-syntax
                    for (const sku of skus) {
                        const p = controller.findPlacement(placement);

                        const c = p.components.find((x) => x.code.includes(sku) && controller.isAvailable(x));

                        if (c) {
                            if (controller.updateComponent(p.code, c.code)) {
                                // Mark group as seen.
                                dispatch('seenUpToGroup', {
                                    controller,
                                    placement: p.code,
                                });

                                return true;
                            }
                        } else {
                            console.log(`>>>>>>>> ${placement} ${sku} was not available`);
                        }
                    }

                    return false;
                };

                const selectPlaceholder = (placement) => {
                    const p = controller.findPlacement(placement);

                    const c = p.components.find((x) => x.placeholder && controller.isAvailable(x));

                    if (c) {
                        if (controller.updateComponent(p.code, c.code)) {
                            // Mark group as seen.
                            dispatch('seenUpToGroup', {
                                controller,
                                placement: p.code,
                            });

                            return true;
                        }
                    }

                    return false;
                };

                const pickHead = () => {
                    const color = controller.selectedAtPlacement(constants.PLACEMENT_SKIN_COLOR);

                    const skinTag = color.tags.find((t) => t.startsWith('skin-'));

                    let bodyTag;

                    if (bodyTag == null) {
                        const body = controller.selectedAtPlacement(constants.PLACEMENT_BODY);

                        if (body && body.tags) {
                            bodyTag = body.tags.includes('male') ? 'head-male' : 'head-female';
                        }
                    }

                    // Find a head.
                    const heads = controller.findPlacement(constants.PLACEMENT_HEAD);

                    if (heads && heads.components) {
                        const head = heads.components.find((c) => {
                            if (c.tags.includes(skinTag) && c.tags.includes(bodyTag)) {
                                if (controller.isAvailable(c)) {
                                    return true;
                                }
                            }

                            return false;
                        });

                        if (head) {
                            dispatch('log', {
                                level: 'info',
                                message: `Found a head ${head.code}`,
                            });

                            controller.updateComponent(constants.PLACEMENT_HEAD, head.code);
                        }
                    }

                    // Check beards.
                    const head = controller.selectedAtPlacement(constants.PLACEMENT_HEAD);

                    if (head && head.custom['fun-sku-beards']) {
                        const beardSku = head.custom['fun-sku-beards'];
                        const beardHead = controller.findPlacement(constants.PLACEMENT_OTHER).components.find((c) => {
                            if (c.code.includes(beardSku)) {
                                return true;
                            }

                            return false;
                        });

                        if (beardHead) {
                            if (!controller.isAvailable(beardHead)) {
                                const selectedBeard = controller.selectedAtPlacement(constants.PLACEMENT_BEARD);
                                if (!selectedBeard.placeholder) {
                                    const beardPlaceholder = controller.findPlacement(constants.PLACEMENT_BEARD).components.find((x) => x.placeholder);

                                    if (beardPlaceholder) {
                                        controller.updateComponent(constants.PLACEMENT_BEARD, beardPlaceholder.code);
                                    }
                                }
                            }
                        }
                    }
                };

                if (!makeSelections(aiSelections.hair, 'POPHair')) {
                    selectPlaceholder('POPHair');
                }

                if (!makeSelections(aiSelections.beard, 'POPBeard')) {
                    selectPlaceholder('POPBeard');
                }

                // Select head _after_ picking a beard.
                pickHead();

                if (!makeSelections(aiSelections.glasses, 'POPGlasses')) {
                    selectPlaceholder('POPGlasses');
                }

                if (!makeSelections(aiSelections.torso, 'POPTorso')) {
                    selectPlaceholder('POPTorso');
                }

                if (!makeSelections(aiSelections.legs, 'POPLegs')) {
                    selectPlaceholder('POPLegs');
                }
            }
        },
    },
};
